import React, { useState, useEffect, useRef } from 'react';
import { ImageLightbox } from '../ui/ImageLightbox';
import {
  Heart, Trash2, X, Loader2, MapPin,
  CheckCircle, CheckCircle2, Globe, Camera, Star,
  MountainSnow, Lock, Unlock, Gavel, ShieldCheck, Navigation,
  ChevronUp, ChevronDown, MoveHorizontal, ArrowLeftRight,
  AlertTriangle, ShieldAlert, Flag, CreditCard, Zap, Moon, Mountain, RefreshCcw, Flame, Coffee, Home, Ban, Route, RotateCcw, Anchor, User, Edit3, Plus, Sparkles, Landmark, Binoculars, MessageSquare, Share2, FolderPlus, HelpCircle,
  StickyNote, PenLine, Save, Calendar, Fish, ExternalLink, Tent, Recycle, FerrisWheel, Droplet
} from 'lucide-react';
import { CampingSpot, AppUser, WikiSpot, CollaborativeMap, SvvCamera, Coordinates } from '../../types';
import { isTemperatureStale } from '../../services/bathingService';
import { useTranslation } from 'react-i18next';
import ReviewSystem from '../ReviewSystem';
import UserAvatar from '../common/UserAvatar';
import VehicleBadge from '../common/VehicleBadge';
import { suggestDeletion, banSpotGlobally, markSpotAsInvalid, subscribeToReviews, subscribeToSpotImages, db, getThumbUrl, logFeatureUsage, fetchGooglePhotosForSpot } from '../../services/firebaseService';
import type { SpotImage } from '../../services/firebaseService';
import { deleteContent } from '../../services/myContentService';
import { isSnuplassSpot } from '../../services/waypointService';
import { isAutocamperSpot, isUserCreatedSpot, isManagedCampingSpot } from '../../utils/autocamperSpots';
import { useLocationImage } from '../../hooks/useLocationImage';
import SpotImageUpload from './SpotImageUpload';
import { APP_URL } from '../../constants';
import { fetchPropertyInfo } from '../../services/matrikkelService';
import { doc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { ensureSpotShareMetadata } from '../../services/shareMetadataService';
import { isAdminUser } from '../../services/accessControl';
import { openExternalNavigation, isIOS } from '../../services/navigationService';
import { Capacitor } from '@capacitor/core';
import { ChargerProximityBlock, RoadConnectionBlock, AccessBarrierBlock, RasteplassFacilities, TrafficWarning, LandownerBlock, HikingTrailsBlock, AttractionsBlock, WebcamBlock, VideoBlock } from './SpotMetadataBlocks';
import { fetchAttractions } from '../../services/wikiService';
import { fetchCamerasNearSpot } from '../../services/svvCameraService';
import WeatherSunBadge from '../WeatherSunBadge';
import ThreeDayWeatherStrip from '../weather/ThreeDayWeatherStrip';
import NatureScoutCard from '../NatureScoutCard';
import { useWildlife } from '../../hooks/useWildlife';
import { subscribeToConditions, addConditionReport, deleteConditionReport, conditionAge, CONDITION_TAGS } from '../../services/conditionService';
import { formatCleanupAge } from '../../services/cleanupService';
import { SpotCondition, ConditionTag } from '../../types';
import ContextualTooltip from '../gamification/ContextualTooltip';
import SpotVisitModal from './SpotVisitModal';
import PastVisitModal from './PastVisitModal';
import VehicleVisitsOverlay from './VehicleVisitsOverlay';
import ExtendStayControl from './ExtendStayControl';
import { isSpotNearby } from '../../services/spotProximity';
import VerifyAfterVisitModal from './VerifyAfterVisitModal';
import { getSpotEnrichmentNeed, needsGooglePhotoRefresh, materializedSpotCovers } from '../../services/spotEnrichment';
import CommercialCampsiteBlock from './CommercialCampsiteBlock';
import ContactInfoBlock from './ContactInfoBlock';
import TrustLine from './TrustLine';
import { selectSpotFacts } from '../../utils/spotFacts';
import { isSpotOccupied, occupancyDaysRemaining } from '../../services/occupancyService';
import { useToast } from '../../contexts/ToastContext';
import { deriveRoadAccessClass } from '../../services/roadAccessClassifier';
import { shouldShowFinlandLegalWarning } from '../../utils/finlandLegalWarning';
import { dkWarningBadges, SEVERITY_CLASS } from '../../utils/denmarkWarnings';
import SpotProximityFacts from './SpotProximityFacts';
import { formatRelativeAge } from '../../utils/relativeTime';
import { photoCreditVariant } from '../../utils/photoCredit';

interface SpotCardProps {
  spot: CampingSpot & { distance?: number };
  isSelected: boolean;
  compact?: boolean;
  activeHikingTrailId: string | null;
  isFavorite: boolean;
  isVisited?: boolean;
  onSelect: (spot: CampingSpot) => void;
  onSelectHikingTrail: (id: string | null) => void;
  onRemoveSpot?: (id: string) => void;
  onToggleFavorite: (e: React.MouseEvent, id: string) => void;
  onToggleVisit?: (e: React.MouseEvent, id: string) => void;
  onAddPastStay?: (id: string, checkInAt: number, checkedOutAt?: number) => Promise<unknown> | void;
  onDeletePastStay?: (id: string, checkInAt: number) => Promise<unknown> | void;
  collaborativeMaps?: CollaborativeMap[];
  onAddToMap?: (e: React.MouseEvent, id: string) => void;
  onReanalyze?: (s: CampingSpot) => void;
  onEdit?: (s: CampingSpot) => void;
  user: AppUser | null;
  onOpenAuth: () => void;
  isAnalyzing?: boolean;
  onSaveSearchResult?: (s: CampingSpot) => void;
  onFitBounds?: (bounds: [[number, number], [number, number]]) => void;
  onCreateRouteFromSpot?: (spot: CampingSpot) => void;
  isCreatingRoute?: boolean;
  isGeneratingMeta?: boolean;
  nearbySpots?: CampingSpot[];
  visitedAt?: number;
  hideShareAction?: boolean;
  hideNavigateAction?: boolean;
  hideNoteAction?: boolean;
  hideCreateRouteAction?: boolean;
  hideReportAction?: boolean;
  onShowNearestBarrier?: (spot: CampingSpot) => void;
  onEnsureSpotPersisted?: (spotId: string) => void;
  onNavigateInApp?: (spot: CampingSpot) => void;
  onMarkOccupied?: (spotId: string, days: number, tags: ConditionTag[]) => void;
  onExtendOccupancy?: (spotId: string, days: number) => void;
  onClearOccupancy?: (spotId: string) => void;
  userPosition?: Coordinates | null;
}

const LinkifiedText: React.FC<{ text: string }> = ({ text }) => {
  if (!text) return null;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return (
    <>
      {parts.map((part, i) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline hover:text-blue-300 break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
};

function getSatelliteTileInfo(lat: number, lon: number, zoom = 16) {
  const n = Math.pow(2, zoom);
  const xExact = ((lon + 180) / 360) * n;
  const x = Math.floor(xExact);
  const latRad = (lat * Math.PI) / 180;
  const yExact = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  const y = Math.floor(yExact);
  
  // Calculate percentage offsets within the current tile
  const offsetX = (xExact - x) * 100;
  const offsetY = (yExact - y) * 100;

  return {
    urls: [
      `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x - 1}`,
      `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
      `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x + 1}`,
    ],
    offsetX,
    offsetY
  };
}

function getSpotCardSatelliteIcon(spot: CampingSpot, size = 16) {
  const type = spot.spotType || '';
  if (type === 'rasteplass' || type === 'snuplass') return <MapPin size={size} className="text-blue-400" />;
  if (type === 'commercial_campsite') return <Home size={size} className="text-amber-400" />;
  if (type === 'coastal') return <Anchor size={size} className="text-cyan-400" />;
  return <Tent size={size} className="text-emerald-400" />;
}

const SpotCard: React.FC<SpotCardProps> = ({
  spot, isSelected, compact = false, activeHikingTrailId, isFavorite, isVisited, onSelect, onSelectHikingTrail, onToggleFavorite,
  onToggleVisit, onAddPastStay, onDeletePastStay, collaborativeMaps, onAddToMap, onRemoveSpot, onReanalyze, onEdit, user, onOpenAuth, isAnalyzing, onSaveSearchResult, onFitBounds, onCreateRouteFromSpot, isCreatingRoute = false, isGeneratingMeta,
  nearbySpots = [], visitedAt, hideShareAction = false, hideNavigateAction = false, hideNoteAction = false, hideCreateRouteAction = false, hideReportAction = false, onShowNearestBarrier, onEnsureSpotPersisted, onNavigateInApp,
  onMarkOccupied, onExtendOccupancy, onClearOccupancy, userPosition,
}) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const checkInCount = typeof spot.checkInCount === 'number' ? spot.checkInCount : 0;
  const checkInNights = typeof spot.checkInNights === 'number' ? spot.checkInNights : 0;
  const [expandedLegal, setExpandedLegal] = useState(false);
  const [showHikingTrails, setShowHikingTrails] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);

  // FIX 1: Ensure this defaults to false
  const [showAttractions, setShowAttractions] = useState(false);

  const [attractions, setAttractions] = useState<WikiSpot[]>([]);
  const [isLoadingAttractions, setIsLoadingAttractions] = useState(false);
  const [noAttractionsFound, setNoAttractionsFound] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReport, setConfirmReport] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'legal' | 'facilities'>('overview');

  useEffect(() => {
    setActiveTab('overview');
  }, [spot.id]);

  const [fullscreenIdx, setFullscreenIdx] = useState<number | null>(null);
  const [spotReviews, setSpotReviews] = useState<any[]>([]);
  const swipeStartXRef = useRef<number | null>(null);
  const [showNatureCard, setShowNatureCard] = useState(false);
  const [showNearby, setShowNearby] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [showEnrich, setShowEnrich] = useState(false);
  const reviewsSectionRef = useRef<HTMLDivElement | null>(null);
  const wildlife = useWildlife(spot.location.latitude, spot.location.longitude);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [localEierform, setLocalEierform] = useState<string | null>(null);
  const [localOwnerName, setLocalOwnerName] = useState<string | null>(null);
  const [propertyFetched, setPropertyFetched] = useState(false);
  const [propertyFetchError, setPropertyFetchError] = useState<string | null>(null);

  // Tilstandsrapporter
  const [conditions, setConditions] = useState<SpotCondition[]>([]);
  const [showConditionPicker, setShowConditionPicker] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showPastVisitModal, setShowPastVisitModal] = useState(false);
  const [showVehicleVisitsOverlay, setShowVehicleVisitsOverlay] = useState(false);
  // Fjernet avstandskrav: brukeren kan oppdatere, sjekke inn og verifisere fra hvor som helst
  const isNearby = true;
  const [showCreatorVehicle, setShowCreatorVehicle] = useState(false);
  const [showNavChoice, setShowNavChoice] = useState(false);

  // Video-thumbnail for spot.videoUrl (Google Photos, etc.)
  const [videoThumb, setVideoThumb] = useState<string | null>(null);
  useEffect(() => {
    if (!spot.videoUrl) { setVideoThumb(null); return; }
    const url = spot.videoUrl.trim();
    try {
      const u = new URL(url);
      if (u.hostname.match(/(?:youtube\.com|youtu\.be)/)) {
        const id = u.hostname === 'youtu.be' ? u.pathname.slice(1) : u.searchParams.get('v');
        if (id) { setVideoThumb(`https://img.youtube.com/vi/${id}/hqdefault.jpg`); return; }
      }
      if (u.hostname.includes('vimeo.com')) {
        const m = u.pathname.match(/\/(\d+)/);
        if (m) { setVideoThumb(`https://vumbnail.com/${m[1]}.jpg`); return; }
      }
    } catch { /* ignore */ }
    // For andre URL-er (Google Photos m.fl.): hent og parse og:image via proxy
    let cancelled = false;
    (async () => {
      try {
        const proxy = `https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy?url=${encodeURIComponent(url)}`;
        const res = await fetch(proxy);
        const html = await res.text();
        const m = html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i)
               || html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+property=["']og:image["']/i);
        if (!cancelled && m?.[1]) setVideoThumb(m[1]);
      } catch { /* stille */ }
    })();
    return () => { cancelled = true; };
  }, [spot.videoUrl]);

  // SVV Webkameraer
  const [nearbyCameras, setNearbyCameras] = useState<(SvvCamera & { _distance: number })[]>([]);
  const [failedCameraIds, setFailedCameraIds] = useState<Set<string>>(new Set());
  const [failedAutoImageUrls, setFailedAutoImageUrls] = useState<Set<string>>(new Set());

  // Private notater (localStorage)
  const NOTE_KEY = `fricamp_note_${spot.id}`;
  const [note, setNote] = useState(() => localStorage.getItem(`fricamp_note_${spot.id}`) || '');
  const [editingNote, setEditingNote] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const saveNote = () => {
    const trimmed = noteInput.trim();
    if (trimmed) localStorage.setItem(NOTE_KEY, trimmed);
    else localStorage.removeItem(NOTE_KEY);
    setNote(trimmed);
    setEditingNote(false);
  };

  // FIX 1: Reset tabs when spot changes to prevent "sticky" open tabs
  useEffect(() => {
    setShowAttractions(false);
    setShowHikingTrails(false);
    setAttractions([]);
    setNoAttractionsFound(false);
    setExpandedLegal(false);
    setShowReviews(false);
    setShowAllBadges(false);
    setLocalEierform(null);
    setLocalOwnerName(null);
    setPropertyFetched(false);
    setPropertyFetchError(null);
    setEditingNote(false);
    setNote(localStorage.getItem(`fricamp_note_${spot.id}`) || '');
    setNearbyCameras([]);
    setFailedCameraIds(new Set());
  }, [spot.id]);

  useEffect(() => {
    setShowAttractions(false);
    setAttractions([]);
    setNoAttractionsFound(false);
  }, [i18n.language]);

  useEffect(() => {
    if (!isSelected) return;
    const unsub = subscribeToReviews(spot.id, (reviews) => {
      setSpotReviews(reviews || []);
    });
    return () => unsub();
  }, [isSelected, spot.id]);

  // Subscribe to UGC images from subcollection
  const [spotImages, setSpotImages] = useState<SpotImage[]>([]);
  useEffect(() => {
    if (!isSelected) return;
    const unsub = subscribeToSpotImages(spot.id, setSpotImages);
    return () => unsub();
  }, [isSelected, spot.id]);

  // On-demand Google Places-bilder — fire-and-forget når kortet velges.
  // Speiler MobileSpotSheet slik at plass-bilder også lastes fra listekortet
  // (backend har 6-mnd throttle via googlePhotosCheckedAt).
  useEffect(() => {
    if (!isSelected || !spot.id) return;
    const SKIP_TYPES = ['forbidden_area', 'snuplass', 'algorithmic'];
    if (SKIP_TYPES.includes(spot.spotType as string)) return;
    if (needsGooglePhotoRefresh(spot)) {
      fetchGooglePhotosForSpot(spot.id).catch(() => {});
    }
  }, [isSelected, spot.id, spot.spotType, spot.googlePhotosCheckedAt, spot.enrichVersion]);

  // Delt stedsnøyaktig bilde-fallback (Mapillary / Street View) — kun når plassen
  // mangler både brukerbilder og et lagret auto-bilde. Brukerbilder vinner alltid.
  const noOwnPhotosForLoc = spotImages.length === 0 && !(spot.photoUrls && spot.photoUrls.length > 0);
  const locationImage = useLocationImage(
    spot.location?.latitude,
    spot.location?.longitude,
    isSelected && noOwnPhotosForLoc && !spot.auto_image_url,
  );

  // Subscribe to condition reports only when spot is selected (reduces idle Firestore listeners)
  useEffect(() => {
    if (!isSelected) return;
    const unsub = subscribeToConditions(spot.id, setConditions);
    return () => unsub();
  }, [isSelected, spot.id]);

  // Fetch nearby SVV webcams when selected
  useEffect(() => {
    if (!isSelected || !spot.location) return;
    let cancelled = false;
    fetchCamerasNearSpot(spot.location, 500).then(cams => {
      if (!cancelled) setNearbyCameras(cams);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isSelected, spot.id]);

  const handleFetchProperty = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (propertyLoading) return;
    setPropertyLoading(true);
    setPropertyFetchError(null);
    try {
      const info = await fetchPropertyInfo(spot.location);
      setLocalEierform(info.eierform ?? 'Ukjent');
      setLocalOwnerName(info.ownerName ?? null);
      setPropertyFetched(true);
      // Cache tilbake til Firestore (beste-innsats, ignorer feil)
      const update = { 'property.eierform': info.eierform, 'property.ownerName': info.ownerName, 'property.gnr': info.gnr, 'property.bnr': info.bnr };
      try { await updateDoc(doc(db, 'spots', spot.id), update); } catch { }
      try { await updateDoc(doc(db, 'cached_spots', spot.id), { 'property.eierform': info.eierform, 'property.ownerName': info.ownerName }); } catch { }
    } catch (err: any) {
      setPropertyFetched(true);
      setPropertyFetchError('Nettverksfeil – prøv igjen');
      setLocalEierform('Ukjent');
    } finally {
      setPropertyLoading(false);
    }
  };

  const reviewCount = spotReviews.length;
  const reviewAverage = reviewCount > 0
    ? (spotReviews.reduce((sum, r) => sum + (Number(r?.rating) || 0), 0) / reviewCount)
    : 0;

  const featureStrings = (Array.isArray(spot.features) ? spot.features : []).map(f => String(f).toUpperCase());
  const isSearchResult = featureStrings.includes("SØKERESULTAT");
  // `sourceLevel: 4` er VERN mot opprydningsskriptene på autocamperpladsene, ikke et
  // brukerbidrag — se utils/autocamperSpots.ts. Uten unntaket fikk kortet
  // «BRUKER»-kilde, bruker-avatar og «lagt til av»-linjen.
  const isAutocamper = isAutocamperSpot(spot);
  const isUserContribution = isUserCreatedSpot(spot);

  const isAdmin = isAdminUser(user);
  const canEdit = !!user;

  const isRasteplass = featureStrings.includes("RASTEPLASS");
  const isFirepit = featureStrings.includes("BÅLPLASS");
  const isGapahuk = featureStrings.includes("GAPAHUK");
  const isPicnic = featureStrings.includes("PICNIC");
  const isUtsikt = featureStrings.includes("UTSIKT");
  const isVeiEnde = featureStrings.includes("VEI-ENDE");
  const isSnuplass = isSnuplassSpot(spot);
  const isQuay = featureStrings.includes("KAI/BRYGGE");
  // Autocamperpladser er bobilplasser og skal ha samme kort som de kommersielle:
  // stjernevurdering i stedet for fricamp-score, kontaktinfo, ingen
  // allemannsretts-analyse. De ligger på samme kartlag (layers.commercialRvParks).
  const isCampingSpot = isManagedCampingSpot(spot);
  // POI-attraksjoner (fornøyelsesparker, museer osv.) er ikke villmarksplasser:
  // de skal IKKE ha fricamp-camping-analyse (score, AI-vurdering, allemannsrett, tags).
  const isAttractionPoi = spot.spotType === 'attraction';
  // Anvist plass: ingen 150 m-flagg, ingen «vær først med bilde»-invitasjon og ingen
  // lovlighets-chip — de gjelder villmarksplasser.
  const isDesignatedCampingArea = ['commercial_campsite', 'rasteplass', 'gapahuk', 'osm_amenity', 'coastal', 'attraction'].includes(spot.spotType) || isAutocamper;

  const score = Math.round(spot.aiResult?.score ?? spot.score);
  // Kombinert stjernevurdering (vår + Google), persistert av rating-triggeren. For
  // kommersielle campingplasser er dette et mer meningsfullt «score» enn fricamp-tallet.
  // Fallback til Google-rating / egne anmeldelser hvis persistert felt ikke er hentet ennå.
  const combinedRating = typeof spot.ratingAverage === 'number' ? spot.ratingAverage
    : typeof spot.googleRating === 'number' ? spot.googleRating
    : reviewCount > 0 ? reviewAverage
    : null;
  const combinedRatingCount = typeof spot.ratingCount === 'number' ? spot.ratingCount
    : (spot.googleRatingCount || reviewCount || 0);

  // «Ren UX»-declutter: score + rating + verifisert slås sammen til ÉN tillit-linje under
  // navnet (samme presedens som markøren, se resolveTrustSignal). Det kursive score-tallet,
  // den kommersielle rating-blokken og Google-rating-chippen undertrykkes, så samme kvalitet
  // ikke sies tre steder. Nå standard-oppførsel (tidligere bak VITE_CALM_CARD).
  const calmCard = true;
  const cardAiSuitable = spot.aiSuitability?.suitable === true
    && spot.isVerified !== true && spot.sourceLevel !== 4 && spot.spotType !== 'confirmed';
  const trustVerifierName = (spot as any).confirmedByName || (spot as any).lastConfirmedByName || null;
  const trustHumanLabel = trustVerifierName
    ? t('spot.verified_by', { name: trustVerifierName, defaultValue: 'Verifisert av {{name}}' })
    : t('spot.verified', 'Verifisert');
  const trustAiLabel = t('spot.ai_recommended', 'AI-anbefalt');
  const elevation = spot.elevation || spot.aiResult?.elevation_moh || null;
  const hasBarrier = spot.legal?.hasBarrier || false;
  const inProtectedArea = spot.legal?.inProtectedArea === true;
  const protectedAreaType = spot.legal?.protectedAreaType;
  const protectedAreaName = spot.legal?.protectedAreaName;
  const protectedAreaTypeLabel = protectedAreaType === 'NATIONALPARK' ? t('spot.nationalpark')
    : protectedAreaType === 'NATURRESERVAT' ? t('spot.naturreservat')
    : protectedAreaType === 'NATURMINNE' ? t('spot.naturminne')
    : t('spot.protected_area');
  const isSleepFriendly = isRasteplass && !isSnuplass && spot.rasteplassMetadata?.toalett && !spot.rasteplassMetadata?.vinterstengt;

  const houseDist = spot.nearestHouse;
    const isTooClose = houseDist < 150 && !isDesignatedCampingArea;

  // Ferskhet: siste aktive livstegn (berikelse, innsjekk eller verifisering).
  // Vises som chip slik at andre brukere ser at plassen faktisk er i bruk.
  const FRESHNESS_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
  const verifiedAtMs = typeof spot.verifiedAt === 'number' ? spot.verifiedAt : 0;
  const freshnessTs = Math.max(spot.lastConfirmedAt ?? 0, spot.lastCheckInAt ?? 0, verifiedAtMs);
  const isFreshlyConfirmed = freshnessTs > 0 && Date.now() - freshnessTs < FRESHNESS_WINDOW_MS;

  // Førstemann-invitasjon: villmarksplasser uten ekte brukerbilde.
  const invitesFirstPhoto = !isSearchResult && !isAttractionPoi && !isDesignatedCampingArea
    && spot.spotType !== 'forbidden_area' && getSpotEnrichmentNeed(spot) === 'photo';
  const spotTags = Array.isArray(spot.tags) ? spot.tags : [];
  const hasStatligFriluft = spotTags.includes('Statlig Friluftsområde');
  const hasStatskog = spotTags.includes('Statskog');

  // Vintersesong-varsel: Vis ved fjellrelaterte steder Nov-Apr
  const isWinterSeason = (() => { const m = new Date().getMonth(); return m >= 10 || m <= 3; })();
  const MOUNTAIN_KEYWORDS = ['fjell', 'topp', 'fjellvei', 'vedlikeholdsvei', 'høyfjellet', 'tindestreif', 'snø', 'alpine'];
  const hasMountainTag = spotTags.some(t => MOUNTAIN_KEYWORDS.some(kw => t.toLowerCase().includes(kw)));
  const isHighLatitude = spot.location.latitude > 68; // Nord-Norge
  const hasWinterWarning = isWinterSeason && (hasMountainTag || isHighLatitude);

  // Bruk lokalt hentet eierform hvis den finnes, ellers det som er lagret på spot
  const effectiveEierform = localEierform ?? spot.property?.eierform;
  const effectiveOwnerName = localOwnerName ?? spot.property?.ownerName;
  const ownerIsUnknown = !isSearchResult && !isRasteplass && !isSnuplass &&
    (!effectiveEierform || effectiveEierform === 'Ukjent');

  const ownerBadge = (() => {
    if (!effectiveEierform || effectiveEierform === 'Ukjent') return null;
    if (effectiveEierform === 'Privat') return {
      label: effectiveOwnerName ? t('spot.owner.private_name', { name: effectiveOwnerName }) : t('spot.owner.private'),
      className: 'text-orange-300 border-orange-500/40 bg-orange-500/10',
      Icon: Home,
    };
    if (effectiveEierform === 'Offentlig') return {
      label: effectiveOwnerName || t('spot.owner.public'),
      className: 'text-blue-300 border-blue-500/40 bg-blue-500/10',
      Icon: Landmark,
    };
    // Statskog vises allerede via tag — bare vis hvis taggen mangler
    if (effectiveEierform === 'Statskog' && !hasStatskog) return {
      label: effectiveOwnerName || t('spot.owner.statskog'),
      className: 'text-green-300 border-green-500/40 bg-green-500/10',
      Icon: Mountain,
    };
    return null;
  })();
  const isUtmarkCategoryTag =
    !isSearchResult &&
    !isAttractionPoi &&
    !isQuay &&
    !isSnuplass &&
    !isRasteplass &&
    !isFirepit &&
    !isPicnic &&
    !isGapahuk &&
    !isUtsikt &&
    !isVeiEnde;

  const normalizedSource = (spot.source || (isUserContribution ? 'user' : spot.spotType === 'algorithmic' ? 'ai' : undefined)) as 'nvdb' | 'osm' | 'user' | 'ai' | undefined;
  const sourceBadge = normalizedSource
    ? normalizedSource === 'nvdb'
      ? { label: 'NVDB', className: 'text-blue-300 border-blue-500/40 bg-blue-500/10' }
      : normalizedSource === 'osm'
        ? { label: 'OSM', className: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10' }
        : normalizedSource === 'user'
          ? { label: t('spot.source_user'), className: 'text-pink-300 border-pink-500/40 bg-pink-500/10' }
          : { label: 'AI', className: 'text-amber-300 border-amber-500/40 bg-amber-500/10' }
    : null;

  const handleReport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { onOpenAuth(); return; }
    if (!confirmReport) { setConfirmReport(true); return; }
    setIsProcessing(true);
    try {
      await suggestDeletion(spot.id, user.uid, "Rapportert via brukergrensesnitt", user.displayName || user.email);
      if (onRemoveSpot) onRemoveSpot(spot.id);
    } catch (err) {
      toast(t('common.error'), 'error');
    } finally {
      setIsProcessing(false);
      setConfirmReport(false);
    }
  };

  const handleAdminDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin) return;
    if (!confirmDelete) { console.log('[ADMIN-DELETE-DESKTOP] First tap for', spot.id); setConfirmDelete(true); return; }
    console.log('[ADMIN-DELETE-DESKTOP] Confirmed for', spot.id);
    setIsProcessing(true);
    try {
      // wpcard- = syntetisk spot fra brukerens eget waypoint — slett fra rett kilde
      // istedenfor å forsøke banSpotGlobally på et ikke-eksisterende cached_spots-doc.
      if (spot.id.startsWith('wpcard-')) {
        const wpId = spot.id.slice('wpcard-'.length);
        console.log('[ADMIN-DELETE-DESKTOP] wpcard- spot — wpId =', wpId);
        if (wpId.startsWith('visit_')) {
          if (!user?.uid) throw new Error('No user for visit delete');
          const visitedSpotId = wpId.slice('visit_'.length);
          const ref = doc(db, 'user_visits', user.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) throw new Error('No visits doc');
          const visits = (snap.data()?.visits || []) as any[];
          const filtered = visits.filter(v => v.spotId !== visitedSpotId);
          await setDoc(ref, { visits: filtered }, { merge: true });
          console.log('[ADMIN-DELETE-DESKTOP] visit removed, before:', visits.length, 'after:', filtered.length);
        } else if (wpId.startsWith('legacy_wp_')) {
          const realId = wpId.slice('legacy_wp_'.length);
          await deleteDoc(doc(db, 'user_waypoints', realId));
          console.log('[ADMIN-DELETE-DESKTOP] legacy waypoint deleted:', realId);
        } else {
          await deleteContent(wpId);
          console.log('[ADMIN-DELETE-DESKTOP] user_content deleted:', wpId);
        }
      } else {
        await banSpotGlobally(spot.id, "Admin sletting");
      }
      if (onRemoveSpot) onRemoveSpot(spot.id);
    } catch (err) {
      console.error('[ADMIN-DELETE-DESKTOP] FAILED for', spot.id, err);
      toast(t('common.error'), 'error');
    } finally {
      setIsProcessing(false);
      setConfirmDelete(false);
    }
  };

  const handleToggleHiking = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSelected) onSelect(spot);

    const newState = !showHikingTrails;
    setShowHikingTrails(newState);
    if (newState) setShowAttractions(false); // Close other tab
    if (!newState) {
      onSelectHikingTrail(null);
      return;
    }

    if (newState && spot.hikingTrails && spot.hikingTrails.length > 0) {
      const activeTrail =
        spot.hikingTrails.find(t => t.id === activeHikingTrailId) ||
        spot.hikingTrails[0];

      // Ensure one trail is active so the dashed line appears immediately.
      onSelectHikingTrail(activeTrail.id);
    }
  };

  const handleToggleAttractions = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSelected) onSelect(spot);
    if (noAttractionsFound) return;

    if (showAttractions) {
      setShowAttractions(false);
      return;
    }

    setShowHikingTrails(false);
    onSelectHikingTrail(null);

    if (attractions.length === 0) {
      setIsLoadingAttractions(true);
      try {
        const results = await fetchAttractions(spot.location);
        if (results.length === 0) {
          setNoAttractionsFound(true);
          setShowAttractions(false);
        } else {
          setAttractions(results);
          setShowAttractions(true);

          // Fit Bounds to show all attractions + spot
          if (onFitBounds && results.length > 0) {
            let minLat = spot.location.latitude;
            let maxLat = spot.location.latitude;
            let minLon = spot.location.longitude;
            let maxLon = spot.location.longitude;

            results.forEach(w => {
              if (w.location.latitude < minLat) minLat = w.location.latitude;
              if (w.location.latitude > maxLat) maxLat = w.location.latitude;
              if (w.location.longitude < minLon) minLon = w.location.longitude;
              if (w.location.longitude > maxLon) maxLon = w.location.longitude;
            });

            onFitBounds([[minLat, minLon], [maxLat, maxLon]]);
          }
        }
      } catch (e) {
        console.warn("Failed to fetch attractions");
      } finally {
        setIsLoadingAttractions(false);
      }
    } else {
      setShowAttractions(true);
      // Fit bounds even if cached
      if (onFitBounds && attractions.length > 0) {
        let minLat = spot.location.latitude;
        let maxLat = spot.location.latitude;
        let minLon = spot.location.longitude;
        let maxLon = spot.location.longitude;

        attractions.forEach(w => {
          if (w.location.latitude < minLat) minLat = w.location.latitude;
          if (w.location.latitude > maxLat) maxLat = w.location.latitude;
          if (w.location.longitude < minLon) minLon = w.location.longitude;
          if (w.location.longitude > maxLon) maxLon = w.location.longitude;
        });

        onFitBounds([[minLat, minLon], [maxLat, maxLon]]);
      }
    }
  };

  const handleShareSpot = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = `${APP_URL}/share/spot/${spot.id}`;
    const shareText = spot.name || 'Fricamp lokasjon';

    // Best effort: generate and persist missing title/description for better share previews.
    ensureSpotShareMetadata(spot).catch((err) => {
      console.warn('Could not ensure share metadata for spot', err);
    });

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          text: shareText,
          url: shareUrl
        });
        logFeatureUsage('share');
        return;
      } catch (err) {
        // User cancelled share dialog.
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast(t('common.link_copied'), 'success');
    } catch (err) {
      window.prompt('Kopier delingslenken:', shareUrl);
    }
  };

  const handleCreateRoute = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateRouteFromSpot?.(spot);
  };

  const getLegalStatus = () => {
    if (isAnalyzing) return { label: t('spot.analyzing'), color: 'text-[var(--text-muted)] border-[var(--border-primary)] bg-[var(--bg-surface-alt)] animate-pulse' };
    if (isRasteplass || isSnuplass) return { label: t('spot.public'), color: 'text-blue-400 border-blue-500/20 bg-blue-500/5' };
    if (featureStrings.includes("GAPAHUK") || isVeiEnde) return { label: t('spot.wilderness'), color: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5' };
    const status = spot.aiResult?.anbefaling || 'SJEKK_SKILT';
    if (status === 'LOVLIG') return { label: t('spot.legal_allowed'), color: 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5' };
    if (status === 'FORBUDT') return { label: t('spot.forbidden'), color: 'text-red-400 border-red-500/20 bg-red-500/5' };
    return { label: t('spot.check_signs'), color: 'text-amber-400 border-amber-500/20 bg-amber-500/5' };
  };

	  // Logic to determine road type display
	  const getRoadTypeTag = () => {
	    if (isRasteplass || isSnuplass || isSearchResult || isUserContribution) return null;
	    const roadAccessClass = deriveRoadAccessClass(spot).roadAccessClass;

	    // Svensk enskild väghållare (NVDB) = privat vei — viktigst å synliggjøre.
	    if (spot.roadOwnerCode === 3) {
	      return { label: t('spot.private_road', 'Privat väg'), color: 'text-amber-400 border-amber-500/40' };
	    }

	    if (roadAccessClass === 'forest_road') {
	      return { label: t('spot.categories.forest_road', 'Skogsbilvei'), color: 'text-amber-300 border-amber-500/30' };
	    }
	    if (roadAccessClass === 'tractor_road') {
	      return { label: t('spot.tractor_road'), color: 'text-[var(--text-secondary)] border-stone-500/30' };
	    }
	    if (roadAccessClass === 'service_road') {
	      return { label: t('spot.service_road', 'Servicevei'), color: 'text-orange-300 border-orange-500/30' };
	    }

	    // Check highway type first
	    if (spot.highway === 'track') {
	      return { label: t('spot.tractor_road'), color: 'text-[var(--text-secondary)] border-stone-500/30' };
	    }

    // Check surface type
    if (spot.surfaceType === 'Grus') {
      return { label: t('spot.gravel_road'), color: 'text-[var(--text-secondary)] border-stone-500/30' };
    }

    if (spot.surfaceType === 'Asfalt') {
      return { label: t('spot.asphalt'), color: 'text-[var(--text-secondary)] border-stone-500/30' };
    }

    // If unknown, fallback to highway if useful
    // Filter out generic road names stored in any language (spots save pre-translated highway values)
    if (spot.highway && spot.highway !== 'Ukjent' && spot.highway !== 'Vei' && spot.highway !== 'Road' && spot.highway !== t('types.road')) {
      const highwayKey = spot.highway.toLowerCase();
      // FIX: Ensure checks for translation existence first or fallback
      const key = `highway.${highwayKey}`;
      const translatedHighway = t(key, { defaultValue: spot.highway.toUpperCase() });
      return { label: translatedHighway, color: 'text-[var(--text-secondary)] border-stone-500/30' };
    }

    return null;
  };

	  const legalStatus = getLegalStatus();
	  const roadTypeTag = getRoadTypeTag();
	  // Finsk lovvarsel: oppdaget plass i Finland utenfor lovlig kjøreflate (vei/p-plass/
	  // rasteplass) får et gult "sjekk lokalt"-merke. Offisielle rasteplasser/parkering
	  // får ingen. Vises IKKE i tillegg til «Privat väg» (roadOwnerCode===3) — unngå duplikat.
	  const hasPrivateRoadBadge = spot.roadOwnerCode === 3 && !isRasteplass && !isSnuplass && !isSearchResult && !isUserContribution;
	  const showFiLegalWarning = shouldShowFinlandLegalWarning(spot as any) && !hasPrivateRoadBadge;
	  const fiLegalWarningLabel = t('spot.fi_legal_warning', 'Finsk lov: overnatting kun på vei/p-plass – sjekk lokalt');
	  const dkWarnings = React.useMemo(() => dkWarningBadges(spot, t as never), [spot, t]);
	  const roadAccessInfo = deriveRoadAccessClass(spot);
	  const hasTractorRoadWarning = roadAccessInfo.roadAccessClass === 'tractor_road';
	  const hasServiceRoadWarning = roadAccessInfo.roadAccessClass === 'service_road';
	  const restrictedRoadWarning =
	    hasTractorRoadWarning
	      ? {
	        badge: '4x4',
	        title: t('spot.tractor_road_permission_title', {
	          defaultValue: 'Traktorveg kan kreve tillatelse fra grunneier før motorisert bruk.',
	        }),
	        text: t('spot.tractor_road_permission_warning', {
	          defaultValue: 'Traktorveg kan være privat eller ha bruksbegrensninger. Innhent grunneiers tillatelse og sjekk skilt før du kjører inn.',
	        }),
	      }
	      : hasServiceRoadWarning
	        ? {
	          badge: 'SRV',
	          title: t('spot.service_road_permission_title', {
	            defaultValue: 'Servicevei kan ha privat eller begrenset adkomst.',
	          }),
	          text: t('spot.service_road_permission_warning', {
	            defaultValue: 'Servicevei kan være privat, intern driftsvei eller ha tilgangsrestriksjoner. Sjekk skilt og avklar bruk før du kjører inn.',
	          }),
	        }
	        : null;
  const farmlandDistance = Number.isFinite(spot.legal?.distToFarmland as number)
    ? Number(spot.legal?.distToFarmland)
    : null;
  const innmarkCheckLabel =
    spot.isInnmark === true
      ? t('spot.legal.innmark_match')
      : spot.isInnmark === false
        ? t('spot.legal.innmark_no_match')
        : t('spot.legal.innmark_none');

  const handleLegalBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedLegal((prev) => !prev);
  };
  /* Helper to process video URLs robustly */
  const getPreviewableVideo = (videoUrl?: string): { type: 'video' | 'videoFile'; url: string } | null => {
    if (!videoUrl) return null;
    let urlToParse = videoUrl.trim();
    if (!urlToParse.match(/^https?:\/\//i)) {
      urlToParse = `https://${urlToParse}`;
    }

    try {
      const url = new URL(urlToParse);
      const host = url.hostname.toLowerCase();

      // YouTube
      if (host.includes('youtube.com')) {
        const id =
          url.searchParams.get('v') ||
          (url.pathname.includes('/shorts/') ? url.pathname.split('/shorts/')[1]?.split('/')[0] : null) ||
          (url.pathname.includes('/live/') ? url.pathname.split('/live/')[1]?.split('/')[0] : null);
        if (id) return { type: 'video', url: `https://www.youtube.com/embed/${id}` };
        // Handle existing embeds
        if (url.pathname.startsWith('/embed/')) return { type: 'video', url: urlToParse };
      }
      if (host.includes('youtu.be')) {
        let id = url.pathname.replace('/', '').trim();
        // Handle trailing slash if present
        if (id.endsWith('/')) id = id.slice(0, -1);
        if (id) return { type: 'video', url: `https://www.youtube.com/embed/${id}` };
      }

      // Vimeo
      if (host.includes('vimeo.com')) {
        const id = url.pathname.split('/').filter(Boolean).pop();
        if (id) return { type: 'video', url: `https://player.vimeo.com/video/${id}` };
      }

      // Direct Files
      if (/\.(mp4|webm|ogg|mov|m3u8)(\?.*)?$/i.test(url.pathname)) {
        return { type: 'videoFile', url: urlToParse };
      }
      return null;
    } catch (e) {
      console.warn('Video URL parsing error:', e);
      return null;
    }
  };

  const asTrimmedString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
  const getReviewVideoUrl = (rev: any): string => {
    return asTrimmedString(rev?.videoUrl || rev?.videoURL || rev?.video || rev?.video_link || rev?.videoLink);
  };
  const getReviewPhotoUrl = (rev: any): string => {
    return asTrimmedString(rev?.photoUrl || rev?.photoURL || rev?.imageUrl || rev?.imageURL);
  };

  const mediaSlides: Array<{
    type: 'video' | 'videoFile' | 'image' | 'svv_camera' | 'auto_image' | 'video_link';
    url: string;
    originalUrl?: string;
    cameraName?: string;
    cameraDistance?: number;
    autoImageCredit?: string;
    autoImageSource?: string;
    autoImageDate?: string;
    autoImageAccuracy?: number;
    videoLinkUrl?: string;
  }> = [];
  const seen = new Set<string>();

  // 1. UGC images from subcollection (highest priority — shown first)
  spotImages.forEach((img) => {
    const url = img.image_url;
    if (url && !seen.has(`image:${url}`)) {
      seen.add(`image:${url}`);
      mediaSlides.push({ type: 'image', url: getThumbUrl(url), originalUrl: url });
    }
  });

  // 1b. Spot videoUrl thumbnail (Google Photos, YouTube, etc.)
  if (spot.videoUrl && videoThumb) {
    const vKey = `video_link:${spot.videoUrl}`;
    if (!seen.has(vKey)) {
      seen.add(vKey);
      mediaSlides.push({ type: 'video_link', url: videoThumb, videoLinkUrl: spot.videoUrl });
    }
  }

  // 2. Review videos
  (Array.isArray(spotReviews) ? spotReviews : []).forEach((rev) => {
    const v = getReviewVideoUrl(rev);
    if (v) {
      const video = getPreviewableVideo(v);
      if (video) {
        const key = `${video.type}:${video.url}`;
        if (!seen.has(key)) {
          seen.add(key);
          mediaSlides.push(video);
        }
      }
    }
  });
  // 3. Review photos
  (Array.isArray(spotReviews) ? spotReviews : []).forEach((rev) => {
    const p = getReviewPhotoUrl(rev);
    if (p && !seen.has(`image:${p}`)) {
      seen.add(`image:${p}`);
      mediaSlides.push({ type: 'image', url: getThumbUrl(p), originalUrl: p });
    }
  });
  // 4. Spot photoUrls from AddSpotModal
  (Array.isArray(spot.photoUrls) ? spot.photoUrls : []).forEach((p) => {
    const url = asTrimmedString(p);
    if (url && !seen.has(`image:${url}`)) {
      seen.add(`image:${url}`);
      mediaSlides.push({ type: 'image', url: getThumbUrl(url), originalUrl: url });
    }
  });
  // 4b. Materialisert UGC-forsidebilde lagret på selve plass-doc'en
  //     (`ugcThumbnailUrl`/`photoUrl`). Kollapsede feed-/listekort abonnerer IKKE på
  //     `images`-underkolleksjonen (kun når kortet er valgt), så uten dette faller de
  //     tilbake til satellitt selv om plassen har ekte bilder. Ekte foto vinner over
  //     auto-hentet bilde. Når underkolleksjonen senere lastes, deduper `seen`-settet.
  const materializedCovers = materializedSpotCovers(spot);
  for (const cover of materializedCovers) {
    if (cover && !seen.has(`image:${cover}`)) {
      seen.add(`image:${cover}`);
      mediaSlides.push({ type: 'image', url: getThumbUrl(cover), originalUrl: cover });
    }
  }
  // 5. Auto API image (only if NO UGC images exist)
  const hasUgcImages =
    spotImages.length > 0 ||
    (spot.photoUrls && spot.photoUrls.length > 0) ||
    materializedCovers.some(Boolean);
  if (!hasUgcImages && spot.auto_image_url) {
    const autoUrl = spot.auto_image_url;
    if (!seen.has(`image:${autoUrl}`) && !failedAutoImageUrls.has(autoUrl)) {
      seen.add(`image:${autoUrl}`);
      mediaSlides.push({
        type: 'auto_image',
        url: autoUrl,
        originalUrl: autoUrl,
        autoImageCredit: spot.auto_image_credit,
        autoImageSource: spot.auto_image_source,
        autoImageDate: spot.auto_image_date,
        autoImageAccuracy: spot.auto_image_accuracy,
      });
    }
  }
  // 5b. Delt stedsnøyaktig fallback (Mapillary / Street View) — kun når verken
  //     brukerbilder eller et lagret auto-bilde finnes.
  if (!hasUgcImages && !spot.auto_image_url && locationImage?.url && !seen.has(`image:${locationImage.url}`) && !failedAutoImageUrls.has(locationImage.url)) {
    seen.add(`image:${locationImage.url}`);
    const isStreetView = locationImage.source === 'streetview';
    mediaSlides.push({
      type: 'auto_image',
      url: locationImage.url,
      originalUrl: locationImage.url,
      autoImageCredit: isStreetView ? 'Google' : (locationImage.credit || undefined),
      autoImageSource: isStreetView ? 'Street View' : 'Mapillary',
      autoImageDate: locationImage.date || undefined,
      // Koordinat-nøyaktig: Street View-pano < 50 m ("fra plassen"), Mapillary ~150 m.
      autoImageAccuracy: isStreetView ? 50 : 150,
    });
  }
  // 6. SVV webcam images (only online cameras that haven't failed)
  nearbyCameras.filter(c => !failedCameraIds.has(c.id)).forEach(cam => {
    const key = `svv_camera:${cam.id}`;
    if (!seen.has(key)) {
      seen.add(key);
      mediaSlides.push({ type: 'svv_camera', url: cam.image_url, cameraName: cam.name, cameraDistance: cam._distance });
    }
  });
  const hasMedia = mediaSlides.length > 0;

  useEffect(() => {
    setActivePhotoIdx(0);
  }, [spot.id, mediaSlides.length]);

  const goPrevMedia = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (mediaSlides.length < 2) return;
    setActivePhotoIdx((prev) => (prev - 1 + mediaSlides.length) % mediaSlides.length);
  };

  const goNextMedia = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (mediaSlides.length < 2) return;
    setActivePhotoIdx((prev) => (prev + 1) % mediaSlides.length);
  };

  // Fullscreen lightbox gallery: only the still images (not videos), navigable.
  const galleryImages = mediaSlides
    .filter((s) => s.type === 'image' || s.type === 'auto_image' || s.type === 'svv_camera')
    .map((s) => (s as any).originalUrl || s.url);

  const openFullscreen = (e: React.MouseEvent, url: string) => {
    e.stopPropagation(); // don't bubble to the card's onSelect (which jumps to the map)
    const i = galleryImages.indexOf(url);
    setFullscreenIdx(i >= 0 ? i : 0);
  };

  const handleMediaTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    swipeStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
  };

  const handleMediaTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (swipeStartXRef.current == null || mediaSlides.length < 2) return;
    const endX = e.changedTouches?.[0]?.clientX;
    if (typeof endX !== 'number') return;
    const delta = endX - swipeStartXRef.current;
    const threshold = 40;
    if (delta <= -threshold) goNextMedia();
    if (delta >= threshold) goPrevMedia();
    swipeStartXRef.current = null;
  };

  const getTranslatedSpotName = (name: string) => {
    if (!name) return name;
    // Check strict uppercase generic names
    const n = name.toUpperCase();
    if (n === 'SKOGSVEI-ENDE') return t('spot.categories.forest_road');
    if (n === 'SNUPLASS' || n === 'SNUPLASS (SVV)') return t('spot.categories.turning_circle');
    if (n === 'LOMME') return t('spot.categories.pocket');
    if (n === 'RASTEPLASS' || n === 'RASTEPLASS (SVV)') return t('spot.categories.rest_area');
    if (n === 'P-PLASS') return t('spot.categories.parking');
    if (n === 'KAI' || n === 'BRYGGE') return t('spot.categories.quay');
    if (n === 'UTSIKT' || n === 'UTSIKTSPUNKT') return t('spot.categories.viewpoint');
    if (n.includes('SKOGSBILVEI')) return t('spot.categories.forest_road');

    return name;
  };

  const generateEnglishAnalysis = () => {
    // Fallback generator for English users when DB has Norwegian text
    const isEnd = featureStrings.includes('VEI-ENDE');
    const isTractor = spot.highway === 'track' || spot.name.includes('TRAKTOR');
    const roadType = isTractor ? "tractor road" : "forest road";

    if (isSnuplass) return "Based on analysis, this is a turning circle. Usually good surface, but keep clear for other vehicles.";
    if (isRasteplass) return "Verified rest area. Usually permitted strictly for resting, not camping (check signs).";

    if (isEnd) return `The spot is located at the end of a ${roadType} in a wilderness area, well away from houses. Suitable for wild camping provided you do not hinder traffic.`;

    return "The spot has been identified in map data as a potential location for wild camping. Please verify local conditions.";
  };

  const getTranslatedAnalysis = (text: string | undefined) => {
    if (!text) return t('spot.waiting_for_analysis');

    // If language is English and text seems Norwegian (contains 'er' or 'en'), generate english
    if (t('common.search') === 'Search' && (text.includes(' er ') || text.includes(' en ') || text.includes('Punktet'))) {
      return generateEnglishAnalysis();
    }
    return text;
  };

  const getTranslatedDescription = (desc: string | undefined) => {
    if (!desc) return desc;
    // Exact matches for system generated descriptions
    if (desc === 'Enden av en skogsvei.') return t('spot.description_forest_road');
    if (desc === 'Enden av en blindvei.') return t('spot.description_road_end');
    if (desc === 'Rasteplass fra SVV.' || desc === 'Rasteplass fra Statens Vegvesen.') return t('spot.description_rest_area');
    if (desc === 'Snuplass fra SVV.' || desc === 'Snuplass fra Statens Vegvesen.') return t('spot.description_turning_circle');
    if (desc === 'Kommersiell campingplass registrert i OpenStreetMap.') return t('spot.description_commercial');
    if (desc === 'Punkt funnet i kartdata.') return t('spot.reasoning_map');
    // Raw translation key stored when i18n wasn't loaded at creation time
    if (desc === 'spot.description_osm') return t('spot.reasoning_map');
    // Re-translate pre-baked Norwegian/English OSM descriptions
    const osmNoMatch = desc.match(/^(.+) funnet i kartdata$/);
    if (osmNoMatch) return t('spot.description_osm', { feature: osmNoMatch[1] });
    const osmEnMatch = desc.match(/^(.+) found in map data$/);
    if (osmEnMatch) return t('spot.description_osm', { feature: osmEnMatch[1] });
    const osmDeMatch = desc.match(/^(.+) in Kartendaten gefunden$/);
    if (osmDeMatch) return t('spot.description_osm', { feature: osmDeMatch[1] });

    return desc;
  };

  // Calm fakta-linje: plassens egne amenities (vann/bål/vei/bilder) samlet til én rolig linje.
  // Absorberer vann-signalet markøren mister i calm-modus. Bildeantall = ekte foto-slides
  // (auto_image/location-fallback har andre typer og teller ikke).
  const calmPhotoCount = mediaSlides.filter((s) => s.type === 'image').length;
  const calmFacts = calmCard
    ? selectSpotFacts({
        nearestWaterM: typeof spot.nearestWater === 'number' ? spot.nearestWater : null,
        isFirepit,
        roadLabel: roadTypeTag?.label ?? null,
        photoCount: calmPhotoCount,
      })
    : [];
  const calmFactMeta: Record<string, { Icon: typeof Droplet; label: string }> = {
    water: { Icon: Droplet, label: t('spot.fact.water', { defaultValue: 'Vann {{d}}', d: typeof spot.nearestWater === 'number' ? (spot.nearestWater < 1000 ? `${Math.round(spot.nearestWater / 10) * 10} m` : `${(spot.nearestWater / 1000).toFixed(1)} km`) : '' }) },
    firepit: { Icon: Flame, label: t('spot.fact.firepit', 'Bålplass') },
    road: { Icon: Route, label: roadTypeTag?.label ?? '' },
    photo: { Icon: Camera, label: t('spot.fact.photos', { count: calmPhotoCount, defaultValue: '{{count}} bilder' }) },
  };

  return (
    <>
      <div
        id={`spot-card-${spot.id}`}
        onClick={() => onSelect(spot)}
        className={`rounded-panel border transition-all duration-500 cursor-pointer relative overflow-hidden ${isAnalyzing ? 'bg-stone-900/50 border-[var(--border-primary)]' : isSelected ? 'bg-[var(--bg-surface)] border-brand/50 shadow-2xl' : 'bg-[#111313]/40 border-[var(--border-primary)] hover:bg-[#1a1d1d]'}`}
      >
        {isSearchResult && (
          <div className="absolute top-0 right-0 p-3 z-10">
            <button
              onClick={(e) => { e.stopPropagation(); onSaveSearchResult?.(spot); }}
              className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg active:scale-125 transition-transform hover:bg-emerald-500"
              title={t('spot.save_location')}
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </div>
        )}

        {hasMedia && isSelected && (
          <div
            className="relative h-32 sm:h-40 md:h-56 group overflow-hidden bg-black"
            onTouchStart={handleMediaTouchStart}
            onTouchEnd={handleMediaTouchEnd}
          >
            {mediaSlides[activePhotoIdx]?.type === 'video_link' ? (
              <a href={mediaSlides[activePhotoIdx].videoLinkUrl} target="_blank" rel="noopener noreferrer" className="block w-full h-full relative">
                <img
                  src={mediaSlides[activePhotoIdx].url}
                  className="w-full h-full object-cover"
                  alt={t('spot.watch_video')}
                />
                <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                  <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-xl">
                    <i className="fa-solid fa-play text-gray-900 text-xl ml-1" />
                  </div>
                </div>
              </a>
            ) : mediaSlides[activePhotoIdx]?.type === 'video' ? (
              <iframe
                src={mediaSlides[activePhotoIdx].url}
                title={`Video ${activePhotoIdx + 1}`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : mediaSlides[activePhotoIdx]?.type === 'videoFile' ? (
              <video
                src={mediaSlides[activePhotoIdx].url}
                className="w-full h-full object-cover"
                controls
                playsInline
                preload="metadata"
              />
            ) : mediaSlides[activePhotoIdx]?.type === 'svv_camera' ? (
              <>
                <img
                  src={mediaSlides[activePhotoIdx].url}
                  onClick={(e) => openFullscreen(e, mediaSlides[activePhotoIdx].url)}
                  className="w-full h-full object-cover cursor-pointer"
                  alt={mediaSlides[activePhotoIdx].cameraName || 'Webkamera'}
                  onError={() => {
                    const camId = nearbyCameras.find(c => c.image_url === mediaSlides[activePhotoIdx]?.url)?.id;
                    if (camId) setFailedCameraIds(prev => new Set(prev).add(camId));
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm px-3 py-2 z-10 pointer-events-none">
                  <div className="flex items-center gap-1.5">
                    <Camera size={10} className="text-cyan-400" />
                    <span className="text-3xs font-black text-white">{mediaSlides[activePhotoIdx].cameraName}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-4xs text-cyan-300">{mediaSlides[activePhotoIdx].cameraDistance} m unna</span>
                    <span className="text-[7px] text-[var(--text-secondary)]">{t("auto.spots.spotcard.kilde_statens_vegvesen", "Kilde: Statens vegvesen")}</span>
                  </div>
                </div>
              </>
            ) : mediaSlides[activePhotoIdx]?.type === 'auto_image' ? (
              <>
                <img
                  src={mediaSlides[activePhotoIdx].url}
                  onClick={(e) => openFullscreen(e, mediaSlides[activePhotoIdx].url)}
                  className="w-full h-full object-cover cursor-pointer transition-transform duration-700 group-hover:scale-105"
                  alt={spot.name}
                  onError={() => {
                    const url = mediaSlides[activePhotoIdx]?.url;
                    if (url) setFailedAutoImageUrls((prev) => new Set(prev).add(url));
                  }}
                />
                {/* Accuracy badge */}
                <div className="absolute top-2 left-2 z-20 px-2.5 py-1 rounded-xl bg-black/60 border border-blue-400/40 backdrop-blur-sm">
                  <span className="text-3xs font-black text-blue-300">
                    {(mediaSlides[activePhotoIdx].autoImageAccuracy ?? 2000) <= 100
                      ? t('spot_images.from_location', 'Bilde fra plassen')
                      : (mediaSlides[activePhotoIdx].autoImageAccuracy ?? 2000) <= 500
                        ? t('spot_images.from_area', 'Bilde fra nærområdet')
                        : t('spot_images.from_region', 'Miljøbilde fra regionen')}
                  </span>
                </div>
                {/* Credit line — vises kun når vi faktisk har attribusjon/dato */}
                {(() => {
                  const slide = mediaSlides[activePhotoIdx];
                  const variant = photoCreditVariant({ credit: slide.autoImageCredit, source: slide.autoImageSource });
                  const creditText = variant?.kind === 'full'
                    ? t('spot_images.photo_credit', { credit: variant.credit, source: variant.source, defaultValue: 'Foto: {{credit}} via {{source}}' })
                    : variant?.kind === 'single'
                      ? `${t('spot.photo', 'Foto')}: ${variant.value}`
                      : '';
                  const dateText = slide.autoImageDate
                    ? t('spot_images.photo_date', { date: slide.autoImageDate, defaultValue: 'Tatt: {{date}}' })
                    : '';
                  if (!creditText && !dateText) return null;
                  return (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm px-3 py-2 z-10 pointer-events-none">
                      <span className="text-4xs text-[var(--text-secondary)]">
                        {creditText}
                        {creditText && dateText ? <> &middot; </> : null}
                        {dateText}
                      </span>
                    </div>
                  );
                })()}
              </>
            ) : (
              <img
                src={mediaSlides[activePhotoIdx]?.url}
                onClick={(e) => openFullscreen(e, mediaSlides[activePhotoIdx]?.originalUrl || mediaSlides[activePhotoIdx]?.url)}
                className="w-full h-full object-cover cursor-pointer transition-transform duration-700 group-hover:scale-105"
                alt={spot.name}
                onError={(e) => {
                  const orig = mediaSlides[activePhotoIdx]?.originalUrl;
                  if (orig && (e.target as HTMLImageElement).src !== orig) {
                    (e.target as HTMLImageElement).src = orig;
                  }
                }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#151818] via-transparent to-transparent opacity-60 pointer-events-none" />
            {reviewCount > 0 && (
              <div className="absolute top-2 right-2 z-20 px-2.5 py-1 rounded-xl bg-black/60 border border-amber-400/40 backdrop-blur-sm flex items-center gap-1.5">
                <Star size={14} className="text-amber-300" fill="currentColor" />
                <span className="text-tiny font-black text-amber-200 leading-none">{reviewAverage.toFixed(1)}</span>
              </div>
            )}

            {mediaSlides.length > 1 && (
              <>
                <button
                  onClick={goPrevMedia}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/55 border border-[var(--border-secondary)] text-white text-sm leading-none"
                  aria-label={t("auto.spots.spotcard.aria-label.forrige", "Forrige")}
                >
                  ‹
                </button>
                <button
                  onClick={goNextMedia}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/55 border border-[var(--border-secondary)] text-white text-sm leading-none"
                  aria-label={t("auto.spots.spotcard.aria-label.neste", "Neste")}
                >
                  ›
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 px-2 py-1 bg-black/30 backdrop-blur-sm rounded-full z-20">
                  {mediaSlides.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setActivePhotoIdx(i); }}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${i === activePhotoIdx ? 'bg-white w-3' : 'bg-white/30'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {!isSelected && (
          <div className="relative h-28 sm:h-36 overflow-hidden bg-stone-900 group">
            {hasMedia ? (
              <img
                src={mediaSlides[0].url}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                alt={spot.name}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const orig = mediaSlides[0].originalUrl;
                  if (orig && (e.target as HTMLImageElement).src !== orig) {
                    (e.target as HTMLImageElement).src = orig;
                  }
                }}
              />
            ) : (
              <div className="relative w-full h-full bg-stone-800 overflow-hidden">
                {(() => {
                  const info = getSatelliteTileInfo(spot.location.latitude, spot.location.longitude, 16);
                  // We have 3 tiles horizontally (x-1, x, x+1). The center tile (x) is the middle one.
                  // By default, a flex container with 3 items of 100% width would be 300% wide.
                  // To center the EXACT coordinates in the middle of the container:
                  // The container is 100% of the parent width. We load 300% width.
                  // We shift it left by 100% (to center on the middle tile), plus the percentage offset within that tile.
                  return (
                    <div className="absolute top-0 bottom-0 flex w-[300%]" style={{ 
                      left: `calc(-100% - ${info.offsetX}% + 50%)`,
                      top: `calc(-${info.offsetY}% + 50%)`
                    }}>
                      {info.urls.map((url, i) => (
                        <img key={i} src={url} alt="" className="h-full w-1/3 object-cover" loading="lazy" decoding="async" />
                      ))}
                    </div>
                  );
                })()}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping pointer-events-none" style={{ margin: '-8px' }} />
                  <div className="absolute inset-0 rounded-full bg-emerald-500/20 border-2 border-emerald-400/50 pointer-events-none" style={{ margin: '-4px' }} />
                  <div className="w-8 h-8 rounded-full bg-stone-950/80 backdrop-blur-sm border-2 border-white/80 shadow-lg flex items-center justify-center text-white transition-transform group-hover:scale-110">
                    {getSpotCardSatelliteIcon(spot, 16)}
                  </div>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#111313] via-transparent to-transparent opacity-80 pointer-events-none" />
            {reviewCount > 0 && (
              <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-lg bg-black/60 border border-amber-400/40 backdrop-blur-sm flex items-center gap-1">
                <Star size={12} className="text-amber-300" fill="currentColor" />
                <span className="text-2xs font-black text-amber-200 leading-none">{reviewAverage.toFixed(1)}</span>
              </div>
            )}
            <div className="absolute top-2 left-2 z-20 px-2 py-0.5 rounded-lg bg-black/60 border border-white/10 backdrop-blur-sm flex items-center gap-1">
              <span className="text-3xs font-bold text-emerald-400 uppercase tracking-wider">
                {hasMedia ? t('spot.photo', 'Foto') : t('spot.satellite', 'Satellitt')}
              </span>
            </div>
          </div>
        )}

        <div className={compact ? 'p-4 md:p-4' : 'p-4 md:p-6'}>
          <div className={`${compact ? 'flex justify-between items-start gap-2 flex-wrap' : 'flex justify-between items-start gap-2'}`}>
            <div className="space-y-1.5 flex-1 pr-2">
              <div className="flex flex-wrap gap-1.5 items-center">
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleFavorite(e, spot.id); }}
                  className={`p-1 transition-transform active:scale-125 ${isFavorite ? 'text-red-500' : 'text-[var(--text-muted)]'}`}
                >
                  <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
                </button>
                {spot.commercialMetadata?.website && (
                  <a
                    href={spot.commercialMetadata.website.startsWith('http') ? spot.commercialMetadata.website : `https://${spot.commercialMetadata.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 transition-all active:scale-125 text-[var(--text-muted)] hover:text-sky-400"
                    title={t('spot.visit_website', 'Besøk nettside')}
                  >
                    <Globe size={17} />
                  </a>
                )}
                {user && onMarkOccupied && isNearby && (
                  <div className="relative flex items-center">
                    <ContextualTooltip
                      isVisible={isSelected && user && (user.totalVisits || 0) === 0 && !isVisited && !tooltipDismissed}
                      onDismiss={() => setTooltipDismissed(true)}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTooltipDismissed(true);
                        if (spot.spotType === 'rasteplass' && !isVisited) {
                          onMarkOccupied!(spot.id, 0, []);
                        } else {
                          setShowVisitModal(true);
                        }
                      }}
                      className={`p-1 transition-all active:scale-125 ${isVisited ? 'text-emerald-500' : 'text-[var(--text-muted)]'}`}
                      title={isVisited ? 'Fjern besøksmarkering' : spot.spotType === 'rasteplass' ? t('spot.rasteplass_check_in', 'Sjekk inn') : 'Marker som besøkt'}
                    >
                      <CheckCircle2 size={17} fill={isVisited ? 'currentColor' : 'none'} />
                    </button>
                    {(spot.visitCount ?? 0) > 0 && (
                      <span className="text-2xs font-black text-emerald-400/70 ml-0.5">{spot.visitCount}</span>
                    )}
                  </div>
                )}
                {/* Etterregistrer besøk: for plasser du har vært på men ikke er i nærheten av nå
                    (den vanlige innsjekk-knappen krever <150m). Skjules når allerede besøkt. */}
                {user && onAddPastStay && !isVisited && !isNearby && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowPastVisitModal(true); }}
                    className="p-1 transition-all active:scale-125 text-[var(--text-muted)] hover:text-emerald-500"
                    title={t('spot.past_visit_button', 'Har vært her tidligere')}
                  >
                    <Calendar size={16} />
                  </button>
                )}
                {!hideNoteAction && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setNoteInput(note);
                      setEditingNote(true);
                      setTimeout(() => noteRef.current?.focus(), 60);
                    }}
                    className={`p-1 transition-all active:scale-125 ${note ? 'text-amber-400' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                    title={note ? 'Rediger notat' : 'Legg til notat'}
                  >
                    <StickyNote size={16} fill={note ? 'currentColor' : 'none'} />
                  </button>
                )}
                {user && onAddToMap && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAddToMap(e, spot.id); }}
                    className="p-1 transition-all active:scale-125 text-[var(--text-muted)] hover:text-sky-400"
                    title={t('myContent.save_title')}
                  >
                    <FolderPlus size={17} />
                  </button>
                )}
                {isSelected && !isSearchResult && (
                  <SpotImageUpload spotId={spot.id} spotName={spot.name} isFirstPhoto={getSpotEnrichmentNeed(spot) === 'photo'} user={user} onOpenAuth={onOpenAuth} />
                )}
                {user && isSelected && isNearby && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowVisitModal(true); }}
                    className={`p-1 transition-all active:scale-125 ${conditions.length > 0 ? 'text-orange-400' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}
                    title={t('spot.conditions.title')}
                  >
                    <Flag size={16} fill={conditions.length > 0 ? 'currentColor' : 'none'} />
                  </button>
                )}
                {!hideShareAction && !isSearchResult && (
                  <button
                    onClick={handleShareSpot}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all font-black text-2xs uppercase tracking-widest ${isSelected
                      ? 'bg-emerald-500 text-stone-950 shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:scale-105 active:scale-95'
                      : 'bg-[var(--bg-surface-alt)] text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/10'
                      }`}
                    title={t('spot.share')}
                  >
                    <Share2 size={14} strokeWidth={isSelected ? 3 : 2} />
                    <span className="hidden sm:inline">{isSelected ? t('spot.share_spot') : t('spot.share')}</span>
                    <span className="sm:hidden">{t('spot.share')}</span>
                  </button>
                )}
                {canEdit && !isSearchResult && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit?.(spot); }}
                    className="p-1.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] rounded-lg text-emerald-400 transition-all"
                    title={spot.createdBy === user.uid || isAdmin ? t('spot.edit_contribution') : t('spot.suggest_changes')}
                  >
                    <Edit3 size={14} />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <h3 className="font-black text-xs md:text-sm text-[var(--text-primary)] break-words">{getTranslatedSpotName(spot.name)}</h3>
                {!calmCard && typeof spot.googleRating === 'number' && (
                  <span className="flex items-center gap-0.5 text-2xs font-bold text-amber-400 flex-shrink-0" title={t('spot.google_rating', 'Google-vurdering')}>
                    <Star size={11} className="fill-amber-400" />
                    {spot.googleRating.toFixed(1)}
                    {spot.googleRatingCount ? <span className="text-[var(--text-muted)] font-medium">({spot.googleRatingCount})</span> : null}
                  </span>
                )}
              </div>

              {/* Calm: ETT tillit-signal under navnet — erstatter score-tall + ★-rating + verifisert. */}
              {calmCard && (
                <TrustLine
                  humanVerified={!!spot.isVerified}
                  aiSuitable={cardAiSuitable}
                  ratingAverage={combinedRating}
                  ratingCount={combinedRatingCount}
                  humanLabel={trustHumanLabel}
                  aiLabel={trustAiLabel}
                />
              )}

              {/* Calm: fakta-linje — plassens amenities samlet, prikk-separert, uten badge-chrome. */}
              {calmCard && calmFacts.length > 0 && (
                <div data-testid="calm-facts" className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2xs font-semibold text-[var(--text-secondary)]">
                  {calmFacts.map((f) => {
                    const meta = calmFactMeta[f.kind];
                    const Icon = meta.Icon;
                    return (
                      <span key={f.kind} className="inline-flex items-center gap-1.5">
                        <Icon size={12} className="text-emerald-500/80" />
                        {meta.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Calm: hensyn-rad — det ENESTE fargede elementet, kun når faktisk for nær bebyggelse. */}
              {calmCard && isTooClose && houseDist !== undefined && (
                <div data-testid="calm-caution" className="flex items-center gap-2 text-2xs font-semibold text-amber-300/90 bg-amber-500/5 rounded-lg px-2.5 py-1.5">
                  <AlertTriangle size={13} className="text-amber-400 flex-shrink-0" />
                  {t('spot.calm_caution_near_house', { count: Math.round(houseDist), defaultValue: 'Under 150 m fra bebyggelse ({{count}} m) — vis hensyn.' })}
                </div>
              )}

              {/* Vehicle visits summary */}
              {spot.vehicleVisits && Object.entries(spot.vehicleVisits).some(([, c]) => c > 0) && (
                <div className="flex flex-wrap items-center gap-1">
                  {Object.entries(spot.vehicleVisits).filter(([, c]) => c > 0).map(([cat, count]) => (
                    <span key={cat} className="inline-flex items-center gap-0.5">
                      <VehicleBadge category={cat as any} compact />
                      <span className="text-4xs font-black text-orange-400/60">×{count}</span>
                    </span>
                  ))}
                  {(spot.vehicleMaxLength ?? 0) > 0 && (
                    <span className="text-4xs font-black text-[var(--text-muted)] uppercase tracking-wide ml-1">
                      {t('spot.longest')}: {Math.round(spot.vehicleMaxLength! / 100)}m
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-3xs font-black uppercase text-[var(--text-muted)]">
                  {spot.distance !== undefined && (() => {
                    const km = spot.distance;
                    const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
                    const etaMin = Math.max(1, Math.round(km * 1.5 / 70 * 60));
                    const etaLabel = etaMin < 60 ? `~${etaMin} min` : `~${Math.floor(etaMin / 60)}t ${String(etaMin % 60).padStart(2, '0')}m`;
                    return (
                      <span className="flex items-center gap-1 text-emerald-500/80 font-black">
                        <MapPin size={10} />
                        <span>{distLabel}</span>
                        {km >= 0.3 && <span className="text-[var(--text-muted)] font-medium normal-case">· {etaLabel}</span>}
                      </span>
                    );
                  })()}
                  {elevation && <span className="flex items-center gap-1"><MountainSnow size={10} /> {elevation}m</span>}
                  <SpotProximityFacts spot={spot} active={isSelected} compact={compact} />

                  {!isSelected && spot.hikingTrails && spot.hikingTrails.length > 0 && (
                    <button
                      onClick={handleToggleHiking}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all whitespace-nowrap ${showHikingTrails && isSelected ? 'bg-orange-500 text-stone-950 border-orange-500' : 'text-orange-500 bg-orange-500/5 border-orange-500/20 hover:border-orange-500/40'}`}
                    >
                      <Mountain size={10} /> <span>{t('spot.tours', { count: spot.hikingTrails.length })}</span>
                    </button>
                  )}

                  {!isSelected && !noAttractionsFound && (
                    <button
                      onClick={handleToggleAttractions}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all whitespace-nowrap ${showAttractions && isSelected ? 'bg-violet-500 text-white border-violet-500' : 'text-violet-500 bg-violet-500/5 border-violet-500/20 hover:border-violet-500/40'}`}
                    >
                      {isLoadingAttractions ? <Loader2 size={10} className="animate-spin" /> : <Landmark size={10} />}
                      <span>{t('spot.attractions')}</span>
                    </button>
                  )}

                  {!isSearchResult && !hasBarrier && <div className="flex items-center gap-1 tracking-tight text-emerald-500"><Unlock size={10} fill="currentColor" className="opacity-80" /><span>{t('spot.open')}</span></div>}
                  {(checkInCount > 0 || checkInNights > 0) && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowVehicleVisitsOverlay(true); }}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/20 bg-amber-500/5 text-amber-400 whitespace-nowrap hover:border-amber-500/40 hover:bg-amber-500/10 transition-colors cursor-pointer"
                      title={t('spot.vehicle_visits.open_hint', 'Se kjøretøy som har vært her')}
                      data-testid="spot-checkin-badge-button"
                    >
                      <Calendar size={10} />
                      {checkInCount > 0 && (
                        <span>{t('spot.checkins_count', { defaultValue: 'Innsjekker' })}: {checkInCount}</span>
                      )}
                      {checkInNights > 0 && (
                        <span className="text-amber-300/80">{t('spot.checkins_nights', { defaultValue: 'Netter' })}: {checkInNights}</span>
                      )}
                    </button>
                  )}
                  {spot.lastCleanedUp && (
                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-teal-500/20 bg-teal-500/5 text-teal-400 whitespace-nowrap">
                      <Recycle size={10} />
                      <span>Ryddet {formatCleanupAge(spot.lastCleanedUp)}</span>
                    </div>
                  )}
                  {isFreshlyConfirmed && (
                    <div
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-500/20 bg-emerald-500/5 text-emerald-400 whitespace-nowrap normal-case"
                      title={t('spot.fresh_confirmed_title', 'Noen har nylig bekreftet at plassen er i bruk')}
                    >
                      <ShieldCheck size={10} />
                      <span>{t('spot.fresh_confirmed', 'Bekreftet')} {formatRelativeAge(freshnessTs, i18n.language)}</span>
                    </div>
                  )}
                </div>

                {/* Calm: hele sekundær-badge-klyngen (kilde/grunneier/vern/villmark/vilt/fiske/
                    «+N mer») foldes bort fra førstevisningen — alt bor i fanene når kortet åpnes. */}
                {!isSelected && !calmCard && (
                <div className="flex flex-wrap items-center gap-1 mt-1 min-w-0">
                  {!isSelected && showAllBadges && sourceBadge && (
                    <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${sourceBadge.className}`}>
                      {sourceBadge.label}
                    </span>
                  )}
                  {!isSelected && invitesFirstPhoto && (
                    <button
                      onClick={(e) => { e.stopPropagation(); user ? setShowEnrich(true) : onOpenAuth(); }}
                      className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-emerald-400 border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 transition-colors"
                      title={t('spot.first_photo_chip_title', 'Ingen har lagt inn bilde av plassen ennå — første bilde gir bonuspoeng')}
                    >
                      <Camera size={10} /> {t('spot.first_photo_chip', 'Bli den første med bilde')}
                    </button>
                  )}
                  {!isSelected && showAllBadges && !ownerBadge && ownerIsUnknown && (
                    propertyFetched ? (
                      // Etter oppslag – ingen data funnet
                      <button
                        onClick={handleFetchProperty}
                        disabled={propertyLoading}
                        title={propertyFetchError ?? t('spot.owner.no_match_title')}
                        className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-[var(--text-muted)] border-stone-600/30 bg-stone-700/10 hover:text-[var(--text-secondary)] hover:border-stone-500/50 transition-colors disabled:opacity-60"
                      >
                        {propertyLoading
                          ? <Loader2 size={10} className="animate-spin" />
                          : <RefreshCcw size={10} />}
                        {propertyLoading ? t('spot.owner.checking') : (propertyFetchError ? t('spot.owner.try_again') : t('spot.owner.no_data'))}
                      </button>
                    ) : (
                      // Ikke hentet ennå – vis klikbar knapp
                      <button
                        onClick={handleFetchProperty}
                        disabled={propertyLoading}
                        title={t('spot.owner.check_owner_title')}
                        className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-[var(--text-secondary)] border-stone-600/40 bg-stone-700/20 hover:text-[var(--text-primary)] hover:border-stone-500/60 transition-colors disabled:opacity-60"
                      >
                        {propertyLoading
                          ? <Loader2 size={10} className="animate-spin" />
                          : <HelpCircle size={10} />}
                        {propertyLoading ? t('spot.owner.checking') : t('spot.owner.unknown_owner')}
                      </button>
                    )
                  )}
                  {hasWinterWarning && (
                    <span
                      className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-cyan-300 border-cyan-500/40 bg-cyan-500/10"
                      title={t('spot.winter_conditions_title')}
                    >
                      <MountainSnow size={10} /> {t('spot.winter_conditions')}
                    </span>
                  )}
                  {!isSelected && hasStatligFriluft && (
                    <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-emerald-300 border-emerald-500/40 bg-emerald-500/10">
                      <i className="fa-solid fa-tree text-[9px]" /> {t('spot.statlig_friluft')}
                    </span>
                  )}
                  {!isSelected && inProtectedArea && (
                    <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10" title={protectedAreaName || protectedAreaTypeLabel}>
                      <i className="fa-solid fa-tree text-[9px]" /> {protectedAreaName ? `${protectedAreaTypeLabel}: ${protectedAreaName}` : protectedAreaTypeLabel}
                    </span>
                  )}
                  {!isSelected && hasStatskog && (
                    <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-green-300 border-green-500/40 bg-green-500/10">
                      <i className="fa-solid fa-mountain text-[9px]" /> {t('spot.statskog')}
                    </span>
                  )}
                  {isUtmarkCategoryTag ? (
                    <button
                      onClick={handleLegalBadgeClick}
                      className="inline-flex items-center text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-brand border-brand/20 bg-brand/5 hover:border-brand/50 transition-colors"
                    >
                      {t('spot.wilderness')}
                    </button>
                  ) : (
                    <span className={`inline-flex items-center text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${isSearchResult ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : isQuay ? 'text-blue-400 border-blue-500/20' : isSnuplass ? 'text-blue-400 border-blue-500/20' : isRasteplass ? 'text-blue-400 border-blue-500/20' : isFirepit ? 'text-orange-400 border-orange-500/20' : isPicnic ? 'text-blue-400 border-blue-500/20' : isGapahuk ? 'text-brand border-brand/20' : isUtsikt ? 'text-brand border-brand/20' : isVeiEnde ? 'text-[var(--text-secondary)] border-stone-500/30' : 'text-brand border-brand/20'}`}>{isSearchResult ? t('spot.search_hit') : isQuay ? t('spot.quay') : isSnuplass ? t('spot.turning_circle') : isRasteplass ? t('spot.picnic') : isFirepit ? t('spot.categories.firepit') : isPicnic ? t('spot.picnic') : isGapahuk ? t('spot.categories.shelter') : isUtsikt ? t('spot.categories.viewpoint') : isVeiEnde ? t('spot.blind_alley') : t('spot.wilderness')}</span>
                  )}

                  {!isSelected && roadTypeTag && (
                    <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${roadTypeTag.color}`}>
                      <Route size={10} /> {roadTypeTag.label}
                    </span>
                  )}

                  {restrictedRoadWarning && (
                    <button
                      onClick={handleLegalBadgeClick}
                      className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 transition-colors"
                      title={restrictedRoadWarning.title}
                    >
                      <AlertTriangle size={10} /> {restrictedRoadWarning.badge}
                    </button>
                  )}

                  {!isSelected && showFiLegalWarning && (
                    <span
                      className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10"
                      title={fiLegalWarningLabel}
                    >
                      <AlertTriangle size={10} /> {fiLegalWarningLabel}
                    </span>
                  )}

                  {/* Danske plassadvarsler. Plassen slettes ikke lenger av regelverket,
                      så merket er det eneste stedet brukeren får vite noe. */}
                  {!isSelected && dkWarnings.map((w) => (
                    <span
                      key={w.key}
                      className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${SEVERITY_CLASS[w.severity]}`}
                      title={w.why || w.label}
                    >
                      <AlertTriangle size={10} /> {w.label}
                    </span>
                  ))}

                  {!isSelected && showAllBadges && isSleepFriendly && <span className="inline-flex items-center text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-blue-400 border-blue-400/30 bg-blue-500/10">{t('spot.recommended_night')}</span>}

                  {!isSelected && showAllBadges && wildlife.species.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); if (!isSelected) onSelect(spot); setActiveTab('overview'); setShowNatureCard(!showNatureCard); }}
                      className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap transition-all ${showNatureCard ? 'bg-emerald-500 text-stone-950 border-emerald-500' : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/60'}`}
                    >
                      <Binoculars size={10} /> {t('spot.wildlife')}
                    </button>
                  )}

                  {!isSelected && showAllBadges && (spot as any).fishingScore && (spot as any).fishingScore.scoreLevel > 0 && (
                    <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${
                      (spot as any).fishingScore.scoreLevel >= 3 ? 'text-blue-600 border-blue-500/30 bg-blue-500/10'
                      : (spot as any).fishingScore.scoreLevel >= 2 ? 'text-blue-400 border-blue-400/30 bg-blue-500/5'
                      : 'text-sky-400 border-sky-400/30 bg-sky-500/5'
                    }`}>
                      <Fish size={10} /> {(spot as any).fishingScore.scoreLabel}
                    </span>
                  )}

                  {/* Calm: hus-avstand flyttet til hensyn-raden under navnet (kun når for nær). */}
                  {!calmCard && !isRasteplass && !isSnuplass && !isSearchResult && houseDist !== undefined && (
                    <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${isTooClose ? 'text-red-400 border-red-500/30 bg-red-500/5' : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5'}`}>
                      <Home size={10} /> {isTooClose ? t('spot.too_close') : t('spot.far_from_house')}
                    </span>
                  )}

                  {!isSearchResult && !isDesignatedCampingArea && (
                    <button
                      onClick={handleLegalBadgeClick}
                      className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${legalStatus.color}`}
                    >
                      {isAnalyzing ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} {legalStatus.label}
                    </button>
                  )}

                  {!isSelected && reviewCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isSelected) onSelect(spot);
                        setShowReviews((prev) => !prev);
                        setTimeout(() => {
                          reviewsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 80);
                      }}
                      className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
                      title={t('spot.reviews_title')}
                    >
                      <Star size={10} fill="currentColor" />
                      {reviewAverage.toFixed(1)} ({reviewCount})
                    </button>
                  )}

                  {/* Amenity-nivå: kollaps «nice-to-have»-badges bak én knapp så
                      kritisk/juridisk + kategori-info ikke drukner i støy (funn #3). */}
                  {(() => {
                    if (isSelected) return null;
                    const amenityCount = [
                      Boolean(sourceBadge),
                      !ownerBadge && ownerIsUnknown,
                      isSleepFriendly,
                      wildlife.species.length > 0,
                      Boolean((spot as any).fishingScore && (spot as any).fishingScore.scoreLevel > 0),
                    ].filter(Boolean).length;
                    if (amenityCount === 0) return null;
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowAllBadges((v) => !v); }}
                        className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-[var(--text-muted)] border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] hover:text-[var(--text-secondary)] transition-colors"
                      >
                        {showAllBadges
                          ? t('spot.badges_less', 'Vis mindre')
                          : t('spot.badges_more', { count: amenityCount, defaultValue: '+{{count}} mer' })}
                      </button>
                    );
                  })()}
                </div>
                )}

                {expandedLegal && !isSearchResult && (
                  <div className="w-full mt-2 bg-emerald-500/5 p-3 rounded-xl border border-emerald-500/20 space-y-2">
                    <p className="text-3xs font-black uppercase tracking-widest text-emerald-400">
                      {t('spot.legal.analysis_title')}
                    </p>
                    <p className="text-2xs text-[var(--text-secondary)]">
                      {t('spot.legal.analysis_desc')}
                    </p>
                    <p className="text-2xs text-[var(--text-secondary)]">
                      {innmarkCheckLabel}
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {houseDist !== undefined && (
                        <span className={`text-3xs font-black px-2 py-1 rounded border ${houseDist < 150 ? 'text-amber-400 border-amber-500/30 bg-amber-500/5' : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5'}`}>
                          {t('spot.legal.house_dist', { count: Math.round(houseDist) })}
                        </span>
                      )}
                      {farmlandDistance !== null && (
                        <span className="text-3xs font-black px-2 py-1 rounded border text-amber-300 border-amber-500/30 bg-amber-500/5">
                          {t('spot.legal.farmland_dist', { count: Math.round(farmlandDistance) })}
                        </span>
                      )}
                    </div>
	                    {restrictedRoadWarning && (
	                      <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-2xs text-amber-100">
	                        <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-300" />
	                        <p className="leading-relaxed">{restrictedRoadWarning.text}</p>
	                      </div>
	                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={`${compact ? 'w-full justify-end mt-0.5' : 'w-auto lg:w-auto'} shrink-0 flex items-center gap-2 lg:justify-end`}>
              {isUserContribution ? (
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); if (spot.creatorVehicleCategory) setShowCreatorVehicle(p => !p); }}
                    title={spot.createdByName || t('spot.anonymous')}
                    className="w-7 h-7 rounded-full p-0 justify-center bg-pink-500/8 border border-pink-500/20 flex items-center text-pink-400 transition-colors hover:bg-pink-500/15"
                  >
                    {/* Vis kun skaperens eget foto — ALDRI fall tilbake til betrakterens
                        (user) avatar/nivå, ellers viser andres plasser ditt eget bilde.
                        Mangler skaper-foto → UserAvatar rendrer nøytralt person-ikon. */}
                    <UserAvatar
                      photoURL={spot.createdByPhoto || undefined}
                      size="xs"
                    />
                  </button>
                  {showCreatorVehicle && spot.creatorVehicleCategory && (
                    <div className="animate-in slide-in-from-top-1 duration-200">
                      <VehicleBadge category={spot.creatorVehicleCategory} length={spot.creatorVehicleLength} is4wd={spot.creatorVehicleIs4wd} compact={compact} />
                    </div>
                  )}
                </div>
              ) : isQuay ? (
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-500 shadow-lg">
                  <Anchor size={22} />
                </div>
              ) : isFirepit ? (
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-500 shadow-lg">
                  <Flame size={22} fill="currentColor" />
                </div>
              ) : isGapahuk ? (
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500 shadow-lg">
                  <i className="fa-solid fa-person-shelter text-lg" />
                </div>
              ) : isUtsikt ? (
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500 shadow-lg">
                  <Mountain size={22} />
                </div>
              ) : isPicnic ? (
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-500 shadow-lg">
                  <Coffee size={22} />
                </div>
              ) : isAttractionPoi ? (
                <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-pink-500/30 flex items-center justify-center text-pink-400 shadow-lg">
                  <FerrisWheel size={22} />
                </div>
              ) : (!calmCard && isCampingSpot && combinedRating != null) ? (
                // Kommersiell campingplass: stjernevurdering (vår + Google) i stedet for fricamp-score.
                <div className="flex flex-col items-end" title={t('spot.rating_from_reviews', 'Vurdering fra anmeldelser')}>
                  <div className="flex items-center gap-1">
                    <Star size={16} className="fill-amber-400 text-amber-400" />
                    <span className="text-lg font-black text-amber-300 leading-none tabular-nums">{combinedRating.toFixed(1)}</span>
                  </div>
                  {combinedRatingCount > 0 && (
                    <span className="text-4xs font-bold text-[var(--text-muted)] mt-0.5 tabular-nums">
                      {combinedRatingCount} {t('spot.rating_count_short', 'vurd.')}
                    </span>
                  )}
                </div>
              ) : (calmCard && !isAnalyzing && !isSearchResult) ? (
                // Calm: fricamp-score-tallet er flyttet til tillit-linjen under navnet.
                // Analyse-loader og søkeresultat-gnist beholdes (ikke tillit-signaler).
                null
              ) : (
                <div className={`text-xl font-black italic ${isAnalyzing && !Number.isFinite(score) ? 'text-emerald-500 animate-pulse' : score >= 8 ? 'text-brand' : score < 4 ? 'text-red-500' : 'text-amber-500'}`}>
                  {isAnalyzing && !Number.isFinite(score) ? <Loader2 className="animate-spin" size={20} /> : isSearchResult ? <Sparkles size={20} className="text-emerald-400" /> : score}
                </div>
              )}
            </div>
          </div>

          {/* 3-dagers værvarsel (soljakt / flerdagsvarsel) */}
          {spot.forecast && spot.forecast.length > 0 && (
            <ThreeDayWeatherStrip forecast={spot.forecast} compact={compact} className="mt-2.5" />
          )}

          {isSelected && (
            <div className="space-y-4 animate-slide-up mt-5 border-t border-[var(--border-primary)] pt-5">
              {/* Tab Menu */}
              <div className="flex w-full bg-[var(--bg-surface-alt)] p-0.5 rounded-xl border border-[var(--border-primary)] flex-shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveTab('overview'); }}
                  className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all whitespace-nowrap text-center ${activeTab === 'overview' ? 'bg-emerald-600 text-white shadow-md font-black' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                >
                  {t('spot.tabs.overview', 'Oversikt')}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveTab('legal'); }}
                  className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all whitespace-nowrap text-center ${activeTab === 'legal' ? 'bg-emerald-600 text-white shadow-md font-black' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                >
                  {t('spot.tabs.area', 'Område')}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveTab('facilities'); }}
                  className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all whitespace-nowrap text-center ${activeTab === 'facilities' ? 'bg-emerald-600 text-white shadow-md font-black' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                >
                  {t('spot.tabs.facilities', 'Fasiliteter')}
                </button>
              </div>

              {activeTab === 'overview' && (
                <>
                  {isSearchResult && (
                    <div className="bg-emerald-500/10 p-5 rounded-panel border border-emerald-500/30 space-y-4 shadow-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/20 rounded-xl text-emerald-400">
                          <Plus size={20} strokeWidth={3} />
                        </div>
                        <div>
                          <p className="text-tiny font-black text-white uppercase tracking-tight">{t('spot.interesting_location')}</p>
                          <p className="text-3xs text-emerald-500/70 font-black uppercase tracking-widest leading-none mt-0.5">{t('spot.add_to_map')}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onSaveSearchResult?.(spot); }}
                        className="w-full bg-emerald-600 text-white py-3.5 rounded-2xl font-black text-2xs uppercase tracking-widest hover:bg-emerald-500 active:scale-95 transition-all shadow-lg"
                      >
                        {t('spot.save_as_discovery')}
                      </button>
                    </div>
                  )}

                  <WeatherSunBadge lat={spot.location.latitude} lon={spot.location.longitude} />

                  {/* Natur / dyreliv — «hvorfor hit», hører hjemme i Oversikt */}
                  {wildlife.species.length > 0 && (
                    <NatureScoutCard
                      lat={spot.location.latitude}
                      lon={spot.location.longitude}
                      species={wildlife.species}
                      latestSeen={wildlife.latestSeen}
                      loading={wildlife.loading}
                      error={wildlife.error}
                    />
                  )}

                  {/* Bade-metadata */}
                  {spot.bathingMetadata && (
                    <div className="space-y-1.5 mt-2">
                      {spot.bathingMetadata.waterTemperature != null && spot.bathingMetadata.waterTemperature > 0 && (
                        <div className="flex items-center gap-2 text-2xs text-[var(--text-secondary)]">
                          <i className="fa-solid fa-temperature-half text-sky-400" style={{ fontSize: 12 }} />
                          {isTemperatureStale(spot.bathingMetadata.temperatureTime)
                            ? <span className="italic">{t('map.bathing.temperature_outdated')}</span>
                            : <span>{t('spot.water_temperature')}: <strong>{spot.bathingMetadata.waterTemperature.toFixed(1)}°C</strong></span>
                          }
                        </div>
                      )}
                      {spot.bathingMetadata.waterQualityWarning && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-2xs text-red-400 font-bold flex items-start gap-2">
                          <i className="fa-solid fa-triangle-exclamation mt-0.5" style={{ fontSize: 11 }} />
                          <div>
                            {spot.bathingMetadata.warningText || t('spot.water_quality_warning')}
                            {spot.bathingMetadata.algalBloom && <div className="font-normal mt-0.5">{t('spot.algal_bloom')}</div>}
                            {spot.bathingMetadata.bathingRestriction && <div className="font-normal mt-0.5">{t('spot.bathing_restriction')}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Occupied banner */}
                  {isSpotOccupied(spot) && (
                    <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2 flex items-center gap-2">
                      <span className="text-red-400 text-sm shrink-0"><i className="fa-solid fa-tent" /></span>
                      <span className="text-tiny font-bold text-red-400">
                        {occupancyDaysRemaining(spot.occupiedUntil!) <= 1
                          ? t('spot.occupied.banner_1')
                          : t('spot.occupied.banner', { days: occupancyDaysRemaining(spot.occupiedUntil!) })}
                      </span>
                      {spot.occupiedBy === user?.uid && onClearOccupancy && (
                        <button
                          onClick={e => { e.stopPropagation(); onClearOccupancy(spot.id); }}
                          className="ml-auto text-2xs text-red-300 underline shrink-0"
                        >
                          {t('spot.occupied.check_out', 'Sjekk ut')}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Forleng oppholdet — når brukeren står her selv (sjekket inn, ikke ut) */}
                  {((isSpotOccupied(spot) && spot.occupiedBy === user?.uid) || (!!user && user.currentCheckIn?.spotId === spot.id)) && onExtendOccupancy && (
                    <div className="mt-2 flex" onClick={e => e.stopPropagation()}>
                      <ExtendStayControl variant="sheet" onExtend={(days) => onExtendOccupancy(spot.id, days)} />
                    </div>
                  )}

                  {/* Private notat */}
                  {!hideNoteAction && (note || editingNote) && (
                    <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/15">
                        <StickyNote size={12} className="text-amber-400 shrink-0" />
                        <span className="text-2xs font-black uppercase tracking-wide text-amber-400">{t('spot.my_note')}</span>
                        <div className="flex-1" />
                        {!editingNote && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setNoteInput(note); setEditingNote(true); setTimeout(() => noteRef.current?.focus(), 60); }}
                              className="p-1 text-amber-600 hover:text-amber-300 transition-colors"
                            ><PenLine size={11} /></button>
                            <button
                              onClick={(e) => { e.stopPropagation(); localStorage.removeItem(NOTE_KEY); setNote(''); }}
                              className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                            ><X size={11} /></button>
                          </>
                        )}
                      </div>
                      {editingNote ? (
                        <div className="p-2 space-y-2" onClick={e => e.stopPropagation()}>
                          <textarea
                            ref={noteRef}
                            value={noteInput}
                            onChange={e => setNoteInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Escape') setEditingNote(false); }}
                            rows={3}
                            placeholder={t('spot.note_placeholder')}
                            className="w-full bg-black/20 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] placeholder-stone-600 focus:outline-none focus:border-amber-500/50 resize-none"
                          />
                          <div className="flex gap-2">
                            <button onClick={(e) => { e.stopPropagation(); saveNote(); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 text-amber-300 rounded-lg text-2xs font-black hover:bg-amber-500/30 transition-colors">
                              <Save size={10} /> {t('spot.save')}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setEditingNote(false); }}
                              className="px-3 py-1.5 text-[var(--text-muted)] rounded-lg text-2xs font-black hover:text-[var(--text-secondary)] transition-colors">
                              {t('spot.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="px-3 py-2.5 text-tiny text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{note}</p>
                      )}
                    </div>
                  )}

                  {isGeneratingMeta && (
                    <div className="bg-emerald-500/10 p-5 rounded-2xl border border-emerald-500/30 flex items-center gap-4">
                      <Loader2 size={20} className="animate-spin text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-tiny font-black text-emerald-400 uppercase tracking-widest leading-none">{t("auto.spots.spotcard.ai_generering", "AI Generering")}</p>
                        <p className="text-3xs text-emerald-500/70 font-black uppercase tracking-widest mt-1">{t("auto.spots.spotcard.lager_navn_og_beskrivelse_base", "Lager navn og beskrivelse basert på AI-analyse...")}</p>
                      </div>
                    </div>
                  )}

                  {spot.description && !isGeneratingMeta && (
                    <div className="bg-[var(--bg-surface-alt)] p-5 rounded-2xl border border-[var(--border-primary)]">
                      <p className="text-[var(--text-secondary)] text-[12px] md:text-tiny leading-relaxed italic">
                        "<LinkifiedText text={getTranslatedDescription(spot.description) || ''} />"
                      </p>
                    </div>
                  )}

                  {/* Kontaktinfo (nettside, telefon, åpningstider, pris) i OVERSIKT for
                      navngitte plasser: attraksjons-POI-er OG kommersielle campingplasser. */}
                  {(isAttractionPoi || isCampingSpot) && <ContactInfoBlock spot={spot} />}

                  {/* Villmarks-egnethets-analysen («vurdering») gjelder ikke navngitte,
                      tilrettelagte plasser: attraksjons-POI-er OG kommersielle campingplasser
                      får aldri denne analysen, så «Venter på analyse…» ville stått evig.
                      Deres nyttige tekst er (den berikede) beskrivelsen over. */}
                  {!isUserContribution && !isSearchResult && !isAttractionPoi && !isCampingSpot && (() => {
                    const vurdering = spot.aiResult?.vurdering;
                    const desc = spot.description;
                    const isDuplicate = vurdering && desc && (
                      vurdering === desc || vurdering.trim() === desc.trim() ||
                      desc.includes(vurdering.slice(0, 60)) || vurdering.includes(desc.slice(0, 60))
                    );
                    if (isDuplicate) return null;
                    return (
                      <div className="bg-emerald-500/5 p-4 rounded-2xl border border-emerald-500/10">
                        {isAnalyzing ? (
                          <div className="flex items-center gap-3">
                            <Loader2 size={12} className="animate-spin text-emerald-500" />
                            <p className="text-emerald-500/70 text-2xs font-black uppercase animate-pulse">{t('spot.analyzing')}</p>
                          </div>
                        ) : spot.aiResult ? (
                          <p className="text-[var(--text-secondary)] text-2xs italic leading-relaxed">
                            "<LinkifiedText text={getTranslatedAnalysis(spot.aiResult.vurdering)} />"
                          </p>
                        ) : (
                          <p className="text-[var(--text-muted)] text-2xs italic leading-relaxed">{t('spot.waiting_for_analysis')}</p>
                        )}
                      </div>
                    );
                  })()}

                  <VideoBlock spot={spot} />

                  {/* ── Brukeromtaler + Google-anmeldelser (flettet, dato-sortert) ── */}
                  {!isSearchResult && (
                    <div ref={reviewsSectionRef} className="mt-2">
                      {(() => {
                        const need = getSpotEnrichmentNeed({ ...spot, reviewCount: spotReviews.length });
                        if (!need) return null;
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!user) { onOpenAuth(); return; }
                              setShowEnrich(true);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-2xs font-bold text-emerald-500 hover:bg-emerald-500/20 transition-colors"
                          >
                            {need === 'photo' ? <Camera size={13} className="shrink-0" /> : <Star size={13} className="shrink-0" />}
                            <span className="text-left flex-1">
                              {need === 'photo'
                                ? t('spot.enrich_nudge_photo', 'Mangler bilde – hjelp oss med ett?')
                                : t('spot.enrich_nudge_rating', 'Vær den første som vurderer plassen')}
                            </span>
                            <Plus size={13} className="shrink-0" />
                          </button>
                        );
                      })()}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowReviews((prev) => !prev);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <MessageSquare size={12} />
                          {t('spot.reviews_title')} {reviewCount > 0 ? `(${reviewCount})` : ''}
                        </span>
                        {showReviews ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {showReviews && (
                        <ReviewSystem
                          spotId={spot.id}
                          spotName={spot.name}
                          user={user}
                          onOpenAuth={onOpenAuth}
                          spotIsVerified={!!spot.isVerified || spot.spotType === 'commercial_campsite' || spot.spotType === 'rasteplass' || spot.spotType === 'osm_amenity' || spot.spotType === 'coastal'}
                          spotHasPhoto={getSpotEnrichmentNeed(spot) !== 'photo'}
                          onOpenVerification={() => onEdit?.(spot)}
                          onEnsurePersisted={spot.externalId ? () => onEnsureSpotPersisted?.(spot.id) : undefined}
                        />
                      )}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'legal' && (
                <>
                  {/* Kategori-/lovlighetsbadges — flyttet hit fra topp-klyngen */}
                  <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${isSearchResult ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : isAttractionPoi ? 'text-pink-300 border-pink-500/40 bg-pink-500/10' : isQuay ? 'text-blue-400 border-blue-500/20' : isSnuplass ? 'text-blue-400 border-blue-500/20' : isRasteplass ? 'text-blue-400 border-blue-500/20' : isFirepit ? 'text-orange-400 border-orange-500/20' : isPicnic ? 'text-blue-400 border-blue-500/20' : isGapahuk ? 'text-brand border-brand/20' : isUtsikt ? 'text-brand border-brand/20' : isVeiEnde ? 'text-[var(--text-secondary)] border-stone-500/30' : 'text-brand border-brand/20'}`}>
                      {isAttractionPoi && <FerrisWheel size={10} />}
                      {isSearchResult ? t('spot.search_hit') : isAttractionPoi ? t(`types.${spot.attractionCategory || 'attraction'}`, t('types.attraction', 'Attraksjon')) : isQuay ? t('spot.quay') : isSnuplass ? t('spot.turning_circle') : isRasteplass ? t('spot.picnic') : isFirepit ? t('spot.categories.firepit') : isPicnic ? t('spot.picnic') : isGapahuk ? t('spot.categories.shelter') : isUtsikt ? t('spot.categories.viewpoint') : isVeiEnde ? t('spot.blind_alley') : t('spot.wilderness')}
                    </span>
                    {!isSearchResult && !isDesignatedCampingArea && (
                      <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${legalStatus.color}`}>
                        {isAnalyzing ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} {legalStatus.label}
                      </span>
                    )}
                    {!isRasteplass && !isSnuplass && !isSearchResult && houseDist !== undefined && !isTooClose && (
                      <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-emerald-400 border-emerald-500/30 bg-emerald-500/5">
                        <Home size={10} /> {t('spot.far_from_house')}
                      </span>
                    )}
                    {hasWinterWarning && (
                      <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-cyan-300 border-cyan-500/40 bg-cyan-500/10" title={t('spot.winter_conditions_title')}>
                        <MountainSnow size={10} /> {t('spot.winter_conditions')}
                      </span>
                    )}
                    {restrictedRoadWarning && (
                      <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10" title={restrictedRoadWarning.title}>
                        <AlertTriangle size={10} /> {restrictedRoadWarning.badge}
                      </span>
                    )}
                    {showFiLegalWarning && (
                      <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10" title={fiLegalWarningLabel}>
                        <AlertTriangle size={10} /> {fiLegalWarningLabel}
                      </span>
                    )}
                  </div>

                  {/* Område-/eiendomsbadges — flyttet hit fra topp-klyngen (funn: rydde toppen) */}
                  {(inProtectedArea || hasStatskog || hasStatligFriluft) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                      {inProtectedArea && (
                        <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-amber-300 border-amber-500/40 bg-amber-500/10" title={protectedAreaName || protectedAreaTypeLabel}>
                          <i className="fa-solid fa-tree text-[9px]" /> {protectedAreaName ? `${protectedAreaTypeLabel}: ${protectedAreaName}` : protectedAreaTypeLabel}
                        </span>
                      )}
                      {hasStatligFriluft && (
                        <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-emerald-300 border-emerald-500/40 bg-emerald-500/10">
                          <i className="fa-solid fa-tree text-[9px]" /> {t('spot.statlig_friluft')}
                        </span>
                      )}
                      {hasStatskog && (
                        <span className="inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-green-300 border-green-500/40 bg-green-500/10">
                          <i className="fa-solid fa-mountain text-[9px]" /> {t('spot.statskog')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* House Proximity Warning Banner */}
                  {isTooClose && houseDist !== undefined && (
                    <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 flex items-center gap-2">
                      <Home size={16} className="text-amber-400 shrink-0" />
                      <span className="text-2xs font-bold text-amber-400 uppercase tracking-tight">
                        {t('spot.house_proximity_warning', { 
                          defaultValue: 'Viktig: Plassen ligger kun {{dist}}m fra bebyggelse. Vis ekstra hensyn!',
                          dist: Math.round(houseDist) 
                        })}
                      </span>
                    </div>
                  )}

                  {/* Tilstandsrapporter */}
                  {conditions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {conditions.map(c => {
                        const meta = CONDITION_TAGS.find(t => t.id === c.tag);
                        if (!meta) return null;
                        const isOwn = c.userId === user?.uid;
                        return (
                          <div
                            key={c.id}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold ${meta.color} ${meta.textColor}`}
                          >
                            <span>{meta.emoji}</span>
                            <span>{t(meta.labelKey)}</span>
                            <span className="opacity-60">· {conditionAge(c.createdAt)}</span>
                            {isOwn && (
                              <button
                                onClick={e => { e.stopPropagation(); deleteConditionReport(c.id); }}
                                className="ml-0.5 opacity-60 hover:opacity-100"
                                title={t('spot.conditions.remove_report')}
                              >×</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isSearchResult && <LandownerBlock spot={spot} />}

                  {/* Turer — listbar direkte i Område */}
                  {spot.hikingTrails && spot.hikingTrails.length > 0 && (
                    <button
                      onClick={handleToggleHiking}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all text-2xs font-black uppercase tracking-widest ${showHikingTrails ? 'bg-orange-500 text-stone-950 border-orange-500' : 'text-orange-400 bg-orange-500/5 border-orange-500/30 hover:border-orange-500/50'}`}
                    >
                      <Mountain size={12} /> {t('spot.tours', { count: spot.hikingTrails.length })}
                    </button>
                  )}
                  {showHikingTrails && (
                    <HikingTrailsBlock
                      spot={spot}
                      activeTrailId={activeHikingTrailId}
                      onTrailClick={(id) => {
                        onSelectHikingTrail(id);
                      }}
                    />
                  )}

                  {/* Nærliggende webkameraer — område-info (var tidligere i Oversikt) */}
                  <WebcamBlock cameras={nearbyCameras} failedIds={failedCameraIds} onImageError={(id) => setFailedCameraIds(prev => new Set(prev).add(id))} />

                  <TrafficWarning spot={spot} />

                  {/* ── Nærliggende steder (kollapsbar) — område-info (var tidligere i Oversikt) ── */}
                  {nearbySpots.length > 0 && (
                    <div className="rounded-xl overflow-hidden mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowNearby(prev => !prev); }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin size={12} />
                          {t('spot.nearby_title')} ({nearbySpots.length})
                        </span>
                        {showNearby ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {showNearby && (
                        <div className="mt-1 border border-white/8 rounded-xl divide-y divide-[var(--border-primary)] overflow-hidden">
                          {nearbySpots.map(ns => (
                            <button
                              key={ns.id}
                              onClick={(e) => { e.stopPropagation(); onSelect(ns); }}
                              className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/3 transition-colors text-left"
                            >
                              <MapPin size={11} className="text-emerald-500/60 shrink-0" />
                              <span className="flex-1 text-tiny text-[var(--text-secondary)] font-medium truncate">{ns.name || t('spot.unnamed')}</span>
                              {ns.distance !== undefined && (
                                <span className="text-2xs text-[var(--text-muted)] shrink-0">
                                  {ns.distance < 1 ? `${Math.round(ns.distance * 1000)} m` : `${ns.distance.toFixed(1)} km`}
                                </span>
                              )}
                              {ns.isVerified && <Star size={10} className="text-amber-400 shrink-0" fill="currentColor" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {activeTab === 'facilities' && (
                <>
                  {/* Severdigheter — flyttet hit fra topp-raden */}
                  {!noAttractionsFound && (
                    <button
                      onClick={handleToggleAttractions}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 mt-3 rounded-xl border transition-all whitespace-nowrap text-2xs font-black uppercase tracking-widest ${showAttractions ? 'bg-violet-500 text-white border-violet-500' : 'text-violet-400 bg-violet-500/5 border-violet-500/30 hover:border-violet-500/50'}`}
                    >
                      {isLoadingAttractions ? <Loader2 size={12} className="animate-spin" /> : <Landmark size={12} />}
                      <span>{t('spot.attractions')}</span>
                    </button>
                  )}
                  {showAttractions && <AttractionsBlock spot={spot} attractions={attractions} />}

                  {/* Kilde/vei/egenskaps-badges — flyttet hit fra topp-klyngen */}
                  {(sourceBadge || roadTypeTag || isSleepFriendly || ((spot as any).fishingScore && (spot as any).fishingScore.scoreLevel > 0)) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                      {sourceBadge && (
                        normalizedSource === 'user' ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); if (spot.creatorVehicleCategory) setShowCreatorVehicle(v => !v); }}
                            className={`inline-flex items-center gap-1.5 text-4xs font-black pl-0.5 pr-2 py-0.5 rounded-full border whitespace-nowrap ${sourceBadge.className} ${spot.creatorVehicleCategory ? 'hover:brightness-110 transition-all cursor-pointer' : 'cursor-default'}`}
                            title={spot.createdByName ? t('spot.added_by', { name: spot.createdByName, defaultValue: 'Lagt til av {{name}}' }) : t('spot.source_user')}
                          >
                            <UserAvatar photoURL={spot.createdByPhoto || undefined} size="xs" />
                            {spot.createdByName
                              ? t('spot.added_by', { name: spot.createdByName, defaultValue: 'Lagt til av {{name}}' })
                              : t('spot.source_user')}
                          </button>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${sourceBadge.className}`}>
                            {sourceBadge.label}
                          </span>
                        )
                      )}
                      {normalizedSource === 'user' && showCreatorVehicle && spot.creatorVehicleCategory && (
                        <VehicleBadge category={spot.creatorVehicleCategory} length={spot.creatorVehicleLength} is4wd={spot.creatorVehicleIs4wd} compact />
                      )}
                      {roadTypeTag && (
                        <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${roadTypeTag.color}`}>
                          <Route size={10} /> {roadTypeTag.label}
                        </span>
                      )}
                      {isSleepFriendly && (
                        <span className="inline-flex items-center text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap text-blue-400 border-blue-400/30 bg-blue-500/10">{t('spot.recommended_night')}</span>
                      )}
                      {(spot as any).fishingScore && (spot as any).fishingScore.scoreLevel > 0 && (
                        <span className={`inline-flex items-center gap-1 text-4xs font-black px-1.5 py-0.5 rounded border whitespace-nowrap ${
                          (spot as any).fishingScore.scoreLevel >= 3 ? 'text-blue-600 border-blue-500/30 bg-blue-500/10'
                          : (spot as any).fishingScore.scoreLevel >= 2 ? 'text-blue-400 border-blue-400/30 bg-blue-500/5'
                          : 'text-sky-400 border-sky-400/30 bg-sky-500/5'
                        }`}>
                          <Fish size={10} /> {(spot as any).fishingScore.scoreLabel}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Visit date */}
                  {isVisited && visitedAt && (
                    <div className="flex items-center gap-1.5 text-2xs text-emerald-600 font-medium mb-3">
                      <Calendar size={11} />
                      <span>{t('spot.my_visit')}: {new Date(visitedAt).toLocaleDateString(i18n.language === 'no' ? 'nb-NO' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}

                  {!isSearchResult && <AccessBarrierBlock spot={spot} onShowOnMap={onShowNearestBarrier ? () => onShowNearestBarrier(spot) : undefined} />}
                  <RoadConnectionBlock spot={spot} />
                  <RasteplassFacilities spot={spot} />
                  <CommercialCampsiteBlock spot={spot} currentUser={user} />
                </>
              )}

              {/* Action buttons (Always visible at the bottom of the card) */}
              <div className="flex flex-col gap-2 border-t border-[var(--border-primary)] pt-4 mt-2">
                {(!hideNavigateAction || !hideCreateRouteAction) && (
                  <div className="flex flex-wrap gap-2">
                    {!hideNavigateAction && (
                      <div className="flex-1 min-w-[110px] relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (Capacitor.isNativePlatform() && onNavigateInApp) {
                              setShowNavChoice(true);
                            } else if (onNavigateInApp) {
                              onNavigateInApp(spot);
                            } else {
                              const { latitude: lat, longitude: lon } = spot.location;
                              openExternalNavigation(lat, lon);
                            }
                          }}
                          className="w-full bg-emerald-600 text-white py-3.5 rounded-xl text-3xs font-black uppercase flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors"
                        >
                          <Navigation size={14} /> {t('spot.navigate')}
                        </button>
                        {showNavChoice && (
                          <>
                            <div className="fixed inset-0 z-[100]" onClick={() => setShowNavChoice(false)} />
                            <div className="absolute bottom-full left-0 right-0 mb-2 z-[101] bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.5)] overflow-hidden">
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowNavChoice(false); onNavigateInApp!(spot); }}
                                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-emerald-500/10 active:bg-emerald-500/10 transition-colors border-b border-[var(--border-primary)]"
                              >
                                <Navigation size={14} className="text-emerald-500" />
                                <span className="text-tiny font-bold text-[var(--text-primary)]">{t('spot.navigate_in_app', 'Naviger i appen')}</span>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowNavChoice(false); const { latitude: lat, longitude: lon } = spot.location; openExternalNavigation(lat, lon); }}
                                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-blue-500/10 active:bg-blue-500/10 transition-colors"
                              >
                                <ExternalLink size={14} className="text-blue-400" />
                                <span className="text-tiny font-bold text-[var(--text-primary)]">{t('spot.open_in_maps', { app: isIOS() ? 'Apple Maps' : 'Google Maps' })}</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {!hideCreateRouteAction && (
                      <button
                        onClick={handleCreateRoute}
                        disabled={isCreatingRoute}
                        className={`flex-1 min-w-[110px] text-white py-3.5 rounded-xl text-3xs font-black uppercase flex items-center justify-center gap-2 transition-colors ${isCreatingRoute ? 'bg-blue-700/70 cursor-wait' : 'bg-blue-600 hover:bg-blue-500'}`}
                      >
                        {isCreatingRoute ? <Loader2 size={14} className="animate-spin" /> : <Route size={14} />}
                        {isCreatingRoute ? t('spot.calculating') : t('spot.create_route')}
                      </button>
                    )}
                  </div>
                )}

                {!hideReportAction && !isSearchResult && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleReport}
                      disabled={isProcessing}
                      className={`flex-1 py-3 rounded-xl text-3xs font-black uppercase border transition-all flex items-center justify-center gap-2 ${confirmReport ? 'bg-amber-600 border-amber-500 text-white animate-pulse' : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-400'}`}
                    >
                      {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                      {confirmReport ? t('spot.confirm_report') : t('spot.report')}
                    </button>
                    {isAdmin && (
                      <button
                        onPointerDown={() => console.log('[ADMIN-DELETE-DESKTOP] BUTTON pointerdown — spot.id =', spot.id, '| isProcessing =', isProcessing, '| confirmDelete =', confirmDelete)}
                        onClick={handleAdminDelete}
                        disabled={isProcessing}
                        className={`flex-1 py-3 rounded-xl text-3xs font-black uppercase border transition-all flex items-center justify-center gap-2 ${confirmDelete ? 'bg-red-600 border-red-500 text-white animate-pulse' : 'bg-red-900/20 border-red-500/20 text-red-500 hover:bg-red-600 hover:text-white'}`}
                      >
                        {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />}
                        {confirmDelete ? t('spot.confirm_report') : t('spot.admin_delete')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Berikelse fra plasskortet (andre inngang, uavhengig av check-in) */}
      {showEnrich && user && (
        <VerifyAfterVisitModal
          spot={spot}
          user={user}
          onClose={() => setShowEnrich(false)}
          onVerified={(result) => {
            setShowEnrich(false);
            toast(
              result.firstPhoto
                ? t('spot.enrich_thanks_first_photo', 'Takk — du la inn det første bildet av plassen! 📸')
                : t('spot.enrich_thanks', 'Takk for bidraget!'),
              'success'
            );
          }}
        />
      )}

      {/* SpotVisitModal */}
      {showVisitModal && user && onMarkOccupied && (
        <SpotVisitModal
          spot={spot}
          user={user}
          conditions={conditions}
          onClose={() => setShowVisitModal(false)}
          onConfirm={({ days, tags }) => {
            onMarkOccupied(spot.id, days, tags);
            setShowVisitModal(false);
          }}
        />
      )}

      {/* PastVisitModal — opphold-editor (Fase 2: flere atskilte opphold m/ fra–til) */}
      {showVehicleVisitsOverlay && (
        <VehicleVisitsOverlay
          spot={spot}
          user={user}
          onClose={() => setShowVehicleVisitsOverlay(false)}
        />
      )}

      {showPastVisitModal && user && onAddPastStay && onDeletePastStay && (
        <PastVisitModal
          spot={spot}
          userId={user.uid}
          onClose={() => setShowPastVisitModal(false)}
          onAddStay={(checkInAt, checkedOutAt) => onAddPastStay(spot.id, checkInAt, checkedOutAt)}
          onDeleteStay={(checkInAt) => onDeletePastStay(spot.id, checkInAt)}
        />
      )}

      {/* Fullscreen image gallery (lightbox) — shared primitive */}
      <ImageLightbox images={galleryImages} index={fullscreenIdx} onIndexChange={setFullscreenIdx} alt={spot.name} />
    </>
  );
};

export default React.memo(SpotCard);
